<template>
  <div class="fn2-page">
    <!-- ============================================================
         A. 权限不足态（账号不是财务/管理员时给出明确可操作的提示）
         ============================================================ -->
    <div class="fn2-denied" v-if="authDenied">
      <div class="fn2-denied__ic"><van-icon name="warning-o" /></div>
      <div class="fn2-denied__t">无财务端访问权限</div>
      <div class="fn2-denied__m">{{ authMsg }}</div>
      <div class="fn2-denied__card">
        <div class="fn2-denied__row"><span>账号</span><b>{{ profile.staff_name || profile.name || '-' }}</b></div>
        <div class="fn2-denied__row"><span>角色</span><b>{{ roleText || '-' }}</b></div>
        <div class="fn2-denied__row"><span>部门</span><b>{{ deptText || '-' }}</b></div>
      </div>
      <div class="fn2-denied__hint">
        财务端开放给：财务人员（角色「财务人员」）/ 财务部门成员 / 财务部门负责人 / 管理员。
        如需开通请联系系统管理员。
      </div>
      <van-button block type="primary" round @click="loadProfile">重新检测</van-button>
      <van-button block plain round @click="onLogout">退出登录</van-button>
    </div>

    <template v-else>
      <!-- ============================================================
           B. 首页：画像 + 快捷入口（含「待办」与未处理气泡）+ 退出登录
           ============================================================ -->
      <template v-if="tabIndex === 0 && !quickTab">
        <!-- 【P112】首页（含快捷入口）下拉刷新：重拉画像 + 「待我审批」数 -->
        <van-pull-refresh v-model="homeRefreshing" @refresh="onHomeRefresh">
        <div class="fn2-hero">
          <div class="fn2-hero__top">
            <span class="fn2-avatar">{{ avatarText }}</span>
            <div class="fn2-hero__id">
              <div class="fn2-hero__name">{{ profile.staff_name || profile.name || '财务人员' }}</div>
              <div class="fn2-hero__dept">{{ deptText }}</div>
            </div>
            <span class="fn2-hero__role">{{ roleText || '财务人员' }}</span>
          </div>
          <!-- 【P113】需求 01：删除「待我审批」+「全部·数据范围」两块 KPI -->
        </div>

        <div class="fn2-quick">
          <div class="fn2-quick__hd">
            <span>快捷入口</span>
            <!-- 【P113】原「刷新」文字链接删除：已由 P112 下拉刷新取代 -->
          </div>
          <div class="fn2-grid">
            <div class="fn2-tile" v-for="q in quickTiles" :key="q.key" @click="quickGo(q)">
              <div class="fn2-tile__ic">
                <span class="fn2-tile__svg" v-html="q.icon"></span>
                <span class="fn2-tile__dot" v-if="q.key === 'msg' && todoCount > 0">{{ todoCount > 99 ? '99+' : todoCount }}</span>
              </div>
              <div class="fn2-tile__label">{{ q.label }}</div>
            </div>
          </div>
          <div class="fn2-acct">
            <div class="fn2-acct__row" @click="onLogout">
              <span class="fn2-acct__ic">
                <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M15 4h3.5v16H15" />
                  <path d="M10.5 8 6.5 12l4 4" />
                  <path d="M6.5 12H15" />
                </svg>
              </span>
              <span class="fn2-acct__txt">
                <span class="fn2-acct__label">退出登录</span>
                <span class="fn2-acct__sub">退出后需重新登录</span>
              </span>
              <van-icon name="arrow" class="fn2-acct__arrow" />
            </div>
          </div>
          <div class="fn2-tip">点击图标可直达该类型全部单据；数据为全公司口径</div>
        </div>
        </van-pull-refresh>
      </template>

      <!-- ============================================================
           C. 统计报表（快捷入口子页）
           ============================================================ -->
      <div class="fn2-stat" v-if="curKey === 'stat'">
        <div class="fn2-subbar">
          <i class="el-icon-arrow-left fn2-topback" @click="backHome"></i>
          <span class="fn2-subbar__t">统计报表</span>
        </div>
        <div class="fn2-stat__range">
          <span>统计区间：{{ stat.range.date_from || '-' }} ~ {{ stat.range.date_to || '-' }}</span>
          <van-icon name="replay" @click="loadStat" />
        </div>
        <div v-if="statLoading" class="fn2-loading"><van-loading size="20px">加载中</van-loading></div>
        <div class="fn2-stat__grid">
          <div class="fn2-stat__card" v-for="c in statCards" :key="c.k">
            <div class="fn2-stat__label">{{ c.label }}</div>
            <div class="fn2-stat__value">{{ c.value }}</div>
            <div class="fn2-stat__extra" v-if="c.extra">{{ c.extra }}</div>
          </div>
        </div>
      </div>

      <!-- ============================================================
           C2.【P92】快捷入口 8 类单据：统一组件（统一引擎 + 全站统一样式）
              与销售端 / 市场端同一套：搜索 / 筛选 / 合计 / 分页 / 详情
           ============================================================ -->
      <bill-list
        v-if="finBillType"
        ref="billList"
        :key="finBillType"
        :bill-type="finBillType"
        scope="all"
        api-prefix="finance"
        :readonly="true"
        :allow-edit="false"
        :show-amount="false"
        @back="backHome"
      />

      <!-- ============================================================
           【P301 全站消息统一】财务端消息 → 改用全站统一组件 MessagePanel
           （P296 曾回退到配置驱动列表；本次按需求再次统一到组件，外观与其它端一致）
           · 4 Tab 固定：未读 → 待处理 → 待阅驳回 → 全部
           · 批量由组件按 Tab 粒度控制（待处理/待阅驳回 永禁批量）
           · 「待处理」取数逻辑保持财务端原有口径（下方 msgTabBody 取数不变） -->
      <div class="fn2-msgwrap" v-if="curKey === 'msg'">
        <message-panel
          title="消息"
          :tabs="msgTabsView"
          :tab="msgTab"
          :list="msgTabRows"
          :loading="msgLoading"
          :refreshing="refreshing"
          :paged="true"
          :finished="msgFinished"
          :list-loading="msgListLoading"
          :err="msgErr"
          :is-core-role="msgIsCoreRole"
          :batch-action="msgTab === 'unread' ? 'read' : 'delete'"
          :show-detail="msgTab === 'pending' || msgTab === 'reject'"
          :show-action="msgTab === 'pending'"
          action-text="去处理"
          @back="backHome"
          @tab="onMsgTab"
          @refresh="loadMsgList(true)"
          @load-more="onMsgLoadMore"
          @open="openMsgDetail"
          @action="openMsgDetail"
          @batch="onMsgBatch"
        />
      </div>

      <!-- ============================================================
           D. 单据列表 —— 头部 / 搜索 / 筛选 / 卡片 全站与销售端（client.vue）一致
           ============================================================ -->
      <template v-if="curKey && curKey !== 'msg' && curKey !== 'stat' && !finBillType">
        <!-- 固定头部：返回 + 标题 + 搜索 + 筛选（复用 client.vue 的 customer-header 类名） -->
        <div class="customer-header" ref="hd">
          <div class="customer-header__top">
            <div class="customer-header__left" @click="isSub ? backHome() : null">
              <i v-if="isSub" class="el-icon-arrow-left fn2-topback"></i>
              <span class="customer-header__title">{{ curTitle }}</span>
            </div>
            <div class="customer-header__search">
              <el-input v-model="kw" class="search-input" :placeholder="'搜索' + curTitle" @change="searchNow">
                <i slot="prefix" class="el-icon-search"></i>
              </el-input>
            </div>
            <!-- 【P202】头部两按钮并列（方案 A）：筛选 + 排序，各 1 层直达。
                 ① 「筛选」取消中间抽屉，点击直接打开高级筛选面板；
                 ② 「排序」独立入口，激活后文案变「金额 ↓」/「日期 ↑」，一眼看出当前排序态；
                 ③ 「筛选」有已选项时显示 N 角标，省掉「点开才发现筛过」。
                 【P203】视觉重塑：原沿用销售端「无底色 + 竖排图标 + 10px 字」的老式按钮，
                 两枚并列后发灰、无主次、图标语义也不对（筛选用的是三横线「展开」图标）。
                 改为**等宽胶囊按钮**：漏斗 + 「筛选」/ 升降序箭头 + 「排序」，
                 未激活浅灰底，激活浅蓝底 + 蓝字，与搜索框构成 iOS 风「搜索框 + 图标胶囊」组。 -->
            <div class="customer-header__actions fn2-hacts">
              <div class="fn2-hbtn" :class="{ 'fn2-hbtn--on': filterActive }" @click="openFilter">
                <van-icon name="filter-o" class="fn2-hbtn__ico" />
                <em class="fn2-hbtn__badge" v-if="advCount > 0">{{ advCount }}</em>
                <span class="fn2-hbtn__txt">筛选</span>
              </div>
              <!-- 【P207】消息列表不需要排序（审批单按时间倒序即可） -->
              <div class="fn2-hbtn" v-if="curKey !== 'msg' && curKey !== 'todo'"
                   :class="{ 'fn2-hbtn--on': sortActive }" @click="openSort">
                <van-icon :name="sortBtnIcon" class="fn2-hbtn__ico" />
                <span class="fn2-hbtn__txt">{{ sortBtnText }}</span>
              </div>
            </div>
          </div>
          <div class="customer-header__tabs" v-if="statusTabs.length > 1">
            <div v-for="(t, i) in statusTabs" :key="i"
                 class="customer-header__tab"
                 :class="{ 'customer-header__tab--active': statusKey === t.value }"
                 @click="onStatusTab(t)">
              {{ t.label }}
              <!-- 【P244】红色气泡角标：仅「未读」「待处理」，「全部」不显示 -->
              <em class="fn2-tab-badge"
                  v-if="curKey === 'msg' && t.value !== '' && msgTabCount(t.value) > 0">{{ msgTabCount(t.value) }}</em>
            </div>
          </div>
        </div>
        <div class="customer-header-spacer" :style="{ height: headerH + 'px' }"></div>

        <!-- 【P191】「回款支付」统一改称「支付单」（P116 已改完底栏/快捷入口，此处同步注释） -->
        <div class="fn2-seg" v-if="false">
        </div>

        <!-- 【P116】销售人员筛选（滑动列表选择，第一项＝全部销售人员）
             需求：支付单 / 开票 / 退款 三个列表的 tab 要有销售人员列表选择，且可以有全部销售人员
             【P179】用户反馈「7 个模块筛选都是用问题的」⇒ 推广为**所有列表常驻**：
               后端已全部支持该维度（支付/开票/退款/会议室/增值走 create_user，
               销售合同/财务合同走 owner_id），且是这几类单据唯一有真实分布的业务维度。
               标签文案随底层列变化，避免用户误以为筛的是同一个人字段。 -->
        <!-- 【P207】消息页也启用常驻选择器：此处语义为「提交人」 -->
        <div class="fn2-owner" v-if="curKey && curKey !== 'stat'">
          <span class="fn2-owner__lb">{{ ownerFieldLabel }}</span>
          <span class="fn2-owner__val" :class="{ 'fn2-owner__val--ph': !ownerId }" @click="openOwnerPick">
            {{ ownerLabel }}<i class="fn2-owner__arrow">▾</i>
          </span>
          <span class="fn2-owner__clear" v-if="ownerId" @click="clearOwner">清除</span>
          <!-- 【P205 2.0001】支付单：批量审核入口（仅当本页存在「待我审核」的行时才显示） -->
          <span class="fn2-owner__batch" v-if="batchSupported && approvableCount > 0"
                :class="{ 'fn2-owner__batch--on': batchMode }" @click="toggleBatch">
            {{ batchMode ? '取消批量' : ('批量审核(' + approvableCount + ')') }}
          </span>
        </div>

        <!-- 生效中的筛选条 -->
        <div class="filter-status-bar" v-if="filterBarVisible">
          <span class="filter-status-bar__txt">{{ filterSummary }}</span>
          <span class="filter-status-bar__clear" @click="clearFilter">清除</span>
        </div>

        <!-- 共 N 条 / 合计
             【P179】用户要求「列表的合计金额先隐藏」⇒ 当时整块移除。
             【P187】用户进一步明确口径后恢复，并改为**服务端「当前筛选条件下全量合计」**：
               · 口径：服务端 listSum（count + sum），与前端已加载页数无关
                 —— 若按已加载页累加，翻页后数字会变大，会误导；
               · 位置：列表头常驻（与「共 N 条」同一行，靠右）；
               · 记忆：localStorage 按列表 key 分别记忆（fn_sum_on_<key>）；
               · 默认：关闭（承接 P179 隐藏口径）。
             开关仅在支持全量合计的 5 类列表显示（后端 _listSpecMap 支持集）。 -->
        <div class="fn2-sum">
          <span>共 {{ listTotal }} 条</span>
          <span class="fn2-sum__right" v-if="sumSupported">
            <template v-if="sumVisible">
              <span class="fn2-sum__label">合计</span>
              <span class="fn2-sum__val">{{ sumText }}</span>
            </template>
            <span class="fn2-sum__toggle" :class="{ 'fn2-sum__toggle--on': sumVisible }" @click="toggleSum">
              <i class="fn2-sum__dot"></i>{{ sumVisible ? '隐藏合计' : '显示合计' }}
            </span>
          </span>
        </div>

        <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
          <div class="list-box">
            <!-- 【P205 2.0001】批量审核操作条（仅支付单） -->
            <div class="fn2-batchbar" v-if="batchSupported && batchMode">
              <span class="fn2-batchbar__txt">已选 {{ batchChecked.length }} 条</span>
              <div class="fn2-batchbar__ops">
                <button type="button" class="fn2-bbtn" @click="toggleBatchAll">全选可审核</button>
                <button type="button" class="fn2-bbtn fn2-bbtn--danger" @click="batchAction('reject')">批量驳回</button>
                <button type="button" class="fn2-bbtn fn2-bbtn--primary" @click="batchAction('approve')">批量审核</button>
                <button type="button" class="fn2-bbtn" @click="exitBatch">取消</button>
              </div>
            </div>
            <!-- 空态：不再出现误导性的「无权限」文案 -->
            <div class="fn2-empty2" v-if="loaded && !rows.length">
              <van-icon :name="curIcon" class="fn2-empty2__ic" />
              <div class="fn2-empty2__txt">暂无{{ curTitle }}</div>
              <div class="fn2-empty2__hint">{{ emptyHint }}</div>
            </div>

            <van-list
              :key="curKey + '-' + _listKey"
              v-model:loading="loading"
              :finished="finished"
              :immediate-check="true"
              :finished-text="rows.length ? '没有更多了' : ''"
              :error.sync="listError"
              error-text="加载失败，点击重试"
              @load="onLoad"
            >
              <!-- 【P419】会议室订单改用全站公共卡片 RoomCard（销售端 roomorder-item 方案）：
                   标题独占整行完整显示、状态标签独立一行且同字去重、左侧无蓝条。
                   财务端只读（后端 readonly=true）⇒ 不传 can-edit / can-delete。
                   金额不脱敏（财务端本就能看金额，列表头还有「显示合计」开关）。 -->
              <template v-if="isRoomList">
                <room-card
                  v-for="(item, i) in rows"
                  :key="'fnr-' + (item.sn || i)"
                  :item="item"
                  @detail="openDetail"
                />
              </template>
              <div v-if="!isRoomList" class="crm-item fn2-item"
                   :class="{ 'fn2-item--batch': batchSupported && batchMode }"
                   v-for="(item, i) in rows" :key="item.sn || i">
                <!-- 【P205 2.0001】批量态勾选（仅「有审批单 + 待我审核」的行可选） -->
                <div class="fn2-check" v-if="batchSupported && batchMode"
                     :class="{ on: batchChecked.indexOf(item.sn) >= 0, off: !rowCanApprove(item) }"
                     @click.stop="onBatchCheck(item)">
                  <van-icon name="success" />
                </div>
                <div class="item-head customer-card"
                     @click="batchSupported && batchMode ? onBatchCheck(item) : openDetail(item)">
                  <div class="customer-card__main">
                    <div class="customer-card__content">
                      <div class="customer-card__header">
                        <div class="customer-card__name-wrap">
                          <span class="customer-card__name">{{ item._title }}</span>
                          <!-- 【P184】`role` 恢复销售端语义：合同类 = 单据/业务类型、会议室 = 时间段、
                               支付/开票/退款 = 产品/类型。金额不再挤在此处（见下方 __amount 独立行）。 -->
                          <span class="customer-card__role" v-if="item._role">{{ item._role }}</span>
                        </div>
                        <span class="status-tag" :class="item._statusClass" v-if="item._status">{{ item._status }}</span>
                      </div>
                      <!-- 【P184-①】标签行：对齐销售端 `.roomorder-flags` / `.contract-status-row`
                           —— 会议室订单＝「收费/免费」+「审核状态」双标签；合同类＝「审核状态」单标签。 -->
                      <div class="customer-card__row item-flags" v-if="item._flagTags && item._flagTags.length">
                        <span v-for="(t, ti) in item._flagTags" :key="ti"
                              class="status-tag" :class="t.cls">{{ t.text }}</span>
                      </div>
                      <div class="customer-card__row">{{ item._row1 }}</div>
                      <!-- 【P184-②】合同类日期行：对齐销售端「签约 / 起 / 止」三段（row--dates） -->
                      <div class="customer-card__row customer-card__row--muted customer-card__row--dates"
                           v-if="item._dates && item._dates.length">
                        <span v-for="(d, di) in item._dates" :key="di">{{ d }}</span>
                      </div>
                      <div class="customer-card__row customer-card__row--muted" v-else>{{ item._row2 }}</div>
                      <!-- 【P184-③】金额行：对齐销售端 `.customer-card__amount` 独立行（标签 + 值） -->
                      <div class="customer-card__amount" v-if="item._amountLabel">
                        <span class="customer-card__amount-label">{{ item._amountLabel }}</span>
                        <span class="customer-card__amount-value">{{ item._amountValue || '-' }}</span>
                      </div>
                    </div>
                  </div>
                  <!-- 【P113】需求 02：列表内【不可操作】——原来的「驳回/通过」按钮已移除，
                       点击卡片 → 详情弹层里做审核（「是直接跳转」） -->
                </div>
              </div>
            </van-list>
          </div>
        </van-pull-refresh>
      </template>

      <!-- ============================================================
           E. 单据详情弹窗（后端直出分组）
           ============================================================ -->
      <!-- 【P116】销售人员滑动列表（第一项＝全部销售人员） -->
      <van-popup v-model="ownerPickShow" round position="bottom">
        <van-picker
          show-toolbar
          title="选择销售人员"
          :columns="ownerColumns"
          @confirm="onOwnerPick"
          @cancel="ownerPickShow=false"
        />
      </van-popup>

      <!-- 【P198】需求 02：详情弹层改用销售端 client.vue 的 .detail-sheet* / .detail-hero*
           / .detail-sec* / .detail-card* 全局类（三个 <style> 块均非 scoped），
           点开形式（蓝渐变头部 + 卡片式分组 + 底部操作条）与销售人员完全一致。
           ★ 严禁在财务端 <style scoped> 内重复定义这些全局类，否则会覆盖全局口径。 -->
      <!-- 【P207】详情全屏 + 右侧划出（对齐销售端客户/商机详情 .cust-detail-pop / .cust-detail__nav） -->
      <van-popup v-model="detailShow" position="right" :style="{ width: '100%', height: '100%' }" class="cust-detail-pop">
        <div class="cust-detail">
          <div class="cust-detail__nav">
            <div class="cust-detail__back" @click="detailShow = false">‹ 返回</div>
            <div class="cust-detail__title">{{ detailTitle }}</div>
            <div class="cust-detail__spacer"></div>
            <div class="cust-detail__action" v-if="detailRemarkEdit === null" @click="startRemark">添加备注</div>
            <div class="cust-detail__spacer" v-else></div>
          </div>
          <div class="cust-detail__body">
            <div v-if="detailLoading" class="fn2-loading"><van-loading size="20px">加载中</van-loading></div>
            <template v-else>
              <!-- 概览：单据标题 + 创建人 / 创建时间（对齐销售端 .detail-hero） -->
              <div class="detail-hero" v-if="detailCreateUser || (detailRow && detailRow.create_time)">
                <div class="detail-hero__main">
                  <div class="detail-hero__name">{{ detailRow && (detailRow._title || detailRow.task_descript) || detailTitle }}</div>
                  <!-- 【P207】创建人 / 创建时间按需拼接：无创建人时不显示「创建人：-」 -->
                  <div class="detail-hero__sub">
                    <template v-if="detailCreateUser">创建人：{{ detailCreateUser }}</template>
                    <template v-if="detailCreateUser && detailRow && detailRow.create_time"> · </template>
                    <template v-if="detailRow && detailRow.create_time">{{ detailRow.create_time }}</template>
                  </div>
                </div>
              </div>

              <!-- 【P207】备注：展示 / 添加 / 编辑 -->
              <div class="detail-sec">
                <div class="detail-sec__title"><span class="detail-sec__bar"></span>备注</div>
                <div class="detail-card">
                  <template v-if="detailRemarkEdit === null">
                    <div class="detail-card__row" v-if="detailRemark">
                      <span class="detail-card__label">备注</span>
                      <span class="detail-card__val">{{ detailRemark }}</span>
                    </div>
                    <div class="fn2-remark-add" v-else @click="startRemark">＋ 添加备注</div>
                  </template>
                  <div class="fn2-remark-edit" v-else>
                    <textarea v-model="detailRemarkDraft" class="fn2-remark-ip" rows="3" placeholder="请输入备注内容"></textarea>
                    <div class="fn2-remark-ops">
                      <button type="button" class="fn2-bbtn" @click="cancelRemark">取消</button>
                      <button type="button" class="fn2-bbtn fn2-bbtn--primary" @click="saveRemark">保存</button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 分组字段：1:1 对齐销售端 .detail-sec + .detail-card -->
              <div class="detail-sec" v-for="(g, gi) in detailGroups" :key="gi">
                <div class="detail-sec__title"><span class="detail-sec__bar"></span>{{ g.name }}</div>
                <div class="detail-cards" v-if="g.fields && g.fields.length">
                  <div class="detail-card">
                    <div class="detail-card__row" v-for="(f, fi) in g.fields" :key="fi">
                      <span class="detail-card__label">{{ f.label }}</span>
                      <span class="detail-card__val">{{ f.value }}</span>
                    </div>
                  </div>
                </div>
                <div class="detail-empty" v-else>暂无可展示字段</div>
              </div>

              <div class="detail-empty" v-if="!detailGroups.length && !detailLoading">该单据暂无可展示字段</div>
            </template>
          </div>
          <div class="fn2-sheet__ft fn2-sheet__ft--solid" v-if="detailCanApprove">
            <van-button block plain type="danger" @click="onReject(detailRow)">驳回</van-button>
            <van-button block type="primary" @click="onApprove(detailRow)">通过</van-button>
          </div>
          <div class="fn2-sheet__ft fn2-sheet__ft--solid" v-else>
            <van-button block plain @click="detailShow = false">关闭</van-button>
          </div>
        </div>
      </van-popup>

      <!-- ============================================================
           E2. 【P100】审批驳回：必填原因（原实现只弹「确认驳回？」，驳回原因无从填写）
           ============================================================ -->
      <van-dialog
        v-model="rejectShow"
        title="审批驳回"
        show-cancel-button
        confirm-button-text="确认驳回"
        confirm-button-color="#e34d59"
        :before-close="onRejectBeforeClose"
      >
        <div class="fn2-reject">
          <div class="fn2-reject__tip">驳回「{{ rejectLabel }}」，请填写原因（将随通知发送给提交人）：</div>
          <van-field
            v-model="rejectRemark"
            type="textarea"
            rows="3"
            maxlength="200"
            show-word-limit
            placeholder="请填写驳回原因（必填）"
          />
        </div>
      </van-dialog>

      <!-- ============================================================
           F. 排序面板（【P202】原「筛选 / 排序」二级抽屉已取消，
              改为独立排序面板；排序项与销售端 van-action-sheet 口径一致）
           ============================================================ -->
      <van-action-sheet
        v-model="sortSheetShow"
        title="排序"
        :actions="sortActions"
        close-on-click-action
        @select="onSortAction"
      >
        <!-- 【P202】当前排序态说明行，避免「点开才知道排的是什么」 -->
        <div class="fn2-sortcur">
          <span class="fn2-sortcur__lb">当前：</span>
          <span class="fn2-sortcur__val">{{ sortCurrentText }}</span>
        </div>
      </van-action-sheet>

      <!-- ============================================================
           【P419】会议室订单筛选：改用全站公共组件 RoomFilter
             · 维度与顺序对齐销售端：使用时间 / 时段 / 是否收费 / 审核状态 /
               会议室 / 门店 / 客户 / 商机 / 销售合同 / 财务合同
             · 门店→会议室二级联动由组件内建（财务端 roomsOptions 支持 stores_sn）
             · 财务端其余 10 类列表仍走下方「高级筛选」面板，行为零变化
           ============================================================ -->
      <room-filter
        v-if="isRoomList"
        v-model="roomFilterShow"
        api-prefix="finance"
        title="筛选会议室订单"
        :applied="roomFilterApplied"
        :applied-meta="roomFilterMeta"
        @apply="onRoomFilterApply"
      />

      <!-- G. 高级筛选（服务端）：按单据类型给出可选筛选项 -->
      <van-popup v-if="!isRoomList" v-model="advShow" round position="bottom" :style="{ height: '72%' }">
        <div class="fn2-sheet">
          <div class="fn2-sheet__hd">
            <span>高级筛选</span>
            <van-icon name="cross" @click="advShow = false" />
          </div>
          <div class="fn2-sheet__bd">
            <div class="fn2-adv" v-for="f in advFields" :key="f.prop">
              <div class="fn2-adv__label">{{ f.label }}</div>
              <!-- 【P175/P187】判空要看该维度实际用的参数键：
                   日期 → <param>_from / <param>_to；其余 → f.param -->
              <div
                class="fn2-adv__val"
                :class="{ 'fn2-adv__val--ph': !advFilled(f) }"
                @click="openAdvPick(f)"
              >
                {{ advText(f) }}
                <i class="fn2-adv__arrow" v-if="advFilled(f) && advClearable(f)" @click.stop="clearAdvField(f)">×</i>
              </div>
            </div>
            <div class="fn2-empty" v-if="!advFields.length">该单据暂无可筛选维度，可用上方状态标签与搜索框。</div>

            <!-- 【P200】「是否显示合计金额」——用户要求把它作为筛选条件的一部分。
                 与列表头右侧的 .fn2-sum__toggle 共用同一状态（sumOn[curKey]）与
                 同一 localStorage 键（fn_sum_on_<key>），两处入口双向同步、不会矛盾。
                 口径刻意与「筛选」区分：合计金额**不参与 where**，只控制列表头右侧
                 合计数字的显隐（数字 = 服务端「当前筛选条件下全量合计」）。 -->
            <div class="fn2-adv fn2-adv--switch" v-if="sumSupported">
              <div class="fn2-adv__label">是否显示合计金额</div>
              <div class="fn2-adv__val fn2-adv__val--switch" @click="toggleSum">
                <span class="fn2-adv__sw" :class="{ 'fn2-adv__sw--on': sumVisible }">
                  <i class="fn2-adv__sw-dot"></i>
                </span>
                <span class="fn2-adv__sw-txt">{{ sumVisible ? '显示' : '隐藏' }}</span>
              </div>
              <div class="fn2-adv__hint">开启后，列表顶部显示「共 N 条」旁的合计金额（口径 = 当前筛选条件下全量）</div>
            </div>
          </div>
          <div class="fn2-sheet__ft">
            <van-button block plain @click="resetAdv">重置</van-button>
            <van-button block type="primary" @click="applyAdv">确定</van-button>
          </div>
        </div>
      </van-popup>

      <!-- H. 高级筛选取值选择器 -->
      <van-popup v-model="advPickShow" round position="bottom">
        <van-picker
          show-toolbar
          :title="advPickTitle"
          :columns="advPickColumns"
          @confirm="onAdvPick"
          @cancel="advPickShow = false"
        />
      </van-popup>

      <!-- H2.【P175】日期区间选择器（高级筛选的「日期」维度走这里） -->
      <van-calendar
        v-model="calendarShow"
        type="range"
        :min-date="calMinDate"
        :max-date="calMaxDate"
        :default-date="advDateDraft.length === 2 ? advDateDraft : null"
        color="#2b5fd9"
        @confirm="onAdvDateConfirm"
      />

      <!-- H3.【P187】通用选择器：关联单号（客户/商机/销售合同/财务合同）/ 门店 / 会议室
           三者共用同一弹层，可搜索（服务端 like 过滤）；会议室带 stores_sn 实现二级联动。
           自研列表（非 van-picker）—— 因为需要「搜索框 + 副标题 + 空态」，van-picker 不支持。 -->
      <van-popup v-model="pickerShow" round position="bottom" :style="{ height: '68%' }">
        <div class="fn2-sheet fn2-sheet--pick">
          <div class="fn2-sheet__hd">
            <span>选择{{ pickerTitle }}</span>
            <van-icon name="cross" @click="pickerShow = false" />
          </div>
          <div class="fn2-pick__search">
            <van-field
              v-model="pickerSearch"
              :placeholder="'搜索' + pickerTitle"
              clearable
              @change="onPickerSearch"
            >
              <template #left-icon><van-icon name="search" /></template>
            </van-field>
          </div>
          <div class="fn2-sheet__bd fn2-pick__bd">
            <div v-if="pickerLoading" class="fn2-loading"><van-loading size="20px">加载中</van-loading></div>
            <template v-else>
              <div class="fn2-pick__item"
                   v-for="(o, oi) in pickerList" :key="oi"
                   :class="{ 'fn2-pick__item--on': String(advValues[pickerField && pickerField.param]) === String(o.value) }"
                   @click="onPickerSelect(o)">
                <div class="fn2-pick__main">
                  <span class="fn2-pick__txt">{{ o.text }}</span>
                  <span class="fn2-pick__sub" v-if="o.sub">{{ o.sub }}</span>
                </div>
                <van-icon name="success" class="fn2-pick__ok"
                          v-if="String(advValues[pickerField && pickerField.param]) === String(o.value)" />
              </div>
              <div class="fn2-empty" v-if="!pickerList.length">没有匹配的{{ pickerTitle }}</div>
            </template>
          </div>
          <div class="fn2-sheet__ft">
            <van-button block plain @click="clearPicker">清除选择</van-button>
            <van-button block type="primary" @click="pickerShow = false">关闭</van-button>
          </div>
        </div>
      </van-popup>
    </template>

    <!-- ============================================================
         I. 底部 Tab 栏：首页 / 支付单 / 开票 / 退款（「待办」已迁至首页快捷入口）
         ============================================================ -->
    <van-tabbar
      v-if="!authDenied"
      v-model="tabIndex"
      active-color="#2b5fd9"
      inactive-color="#7d879c"
      @change="onTabbarChange"
    >
      <van-tabbar-item v-for="t in bottomTabs" :key="t.label" :badge="tabBadgeOf(t.key)">
        {{ t.label }}
        <template #icon>
          <van-icon :name="t.icon" />
        </template>
      </van-tabbar-item>
    </van-tabbar>
  </div>
</template>

<script>
import Vue from 'vue'
import * as financeApi from '@/api/finance.js'
import { getProfile, logout } from '@/util/clientAuth.js'
import { suppressGlobalError } from '@/util/request.js'
// 【P92】全站单据列表统一组件（8 类共用；销售/财务/市场三端一致）
import BillList from '@/components/BillList.vue'
/* 【P419】会议室订单筛选/卡片公共组件（与销售/运营/市场同一套方案） */
import RoomFilter from '@/components/RoomFilter.vue'
import RoomCard from '@/components/RoomCard.vue'
/* 【P301 全站消息统一】全站统一消息组件（销售/市场/财务/运营共用） */
import MessagePanel from '@/components/MessagePanel.vue'
/* 【P415】全局 WS 单例：财务端此前完全没有接入，导致消息气泡无法实时刷新 */
import crmWs from '@/util/websocket.js'

/* 【P92】财务端快捷入口 key → 统一引擎 bill_type
   todo / stat / sellContracts / purchase 属财务端专属能力（待办审批、统计、销售合同、采购单），
   不在统一引擎 8 类内，继续走财务端原有实现 */
/* 【P187-fix】原 FIN_BILL_TYPE 把 快捷入口 7 类全部交给 BillList 统一引擎，
   导致 P187 的「ADV_FIELDS 高级筛选 + 门店↔会议室二级联动 + 服务端全量合计开关」
   对 会议室订单 / 增值订单 完全失效（用户点名的 5 个列表里，这两类只能从快捷入口进）。

   定版口径：把 P187 已覆盖的 4 类（room / valueAdded / finContracts / payments）
   收敛回**财务端自研列表**（与本页底栏「支付单/开票/退款」同一套 .fn2-sum / ADV_FIELDS
   实现，能力完全一致），BillList 仅保留给尚未纳入 P187 改造的 3 类
   （plans 回款计划 / invoices 开票 / refunds 退款 —— 底栏已有自研入口，快捷入口不再重复用引擎）。

   ⚠️ 保留常量以便回滚；真实生效映射见 BILL_ENGINE_TYPES。 */
const BILL_ENGINE_TYPES = {
  /* 回款计划：BillList 引擎（财务端自研列表暂无该项） */
  plans: 'collection'
}

/* 历史映射（仅供对照/回滚参考，不再直接使用） */
// eslint-disable-next-line no-unused-vars
const FIN_BILL_TYPE_LEGACY = {
  payments: 'payment',
  plans: 'collection',
  invoices: 'invoice',
  refunds: 'refund',
  finContracts: 'contract',
  room: 'room',
  valueAdded: 'value'
}

/* ---------------- 列表配置：key → 标题 / 接口 / 详情类型 / 状态筛选项 ---------------- */
const LISTS = {
  /* 【P113】需求 02：消息列表 = 原「待办」数据（financeTodoList），只是呈现与交互变了：
     列表卡片【不再放审核按钮】，点卡片 → 打开详情弹层做审核。 */
  /* 【P207】消息：statusParam 改 ap_status（状态视图「待我处理 / 已处理」由它驱动） */
  msg:           { title: '消息',       icon: 'chat-o',          api: 'financeMsgList',               detailType: 'approval',        statusParam: 'msg_status', sum: '' },
  todo:          { title: '待办',       icon: 'todo-list-o',     api: 'financeTodoList',              detailType: 'approval',        statusParam: '',        sum: '' },
  payments:      { title: '支付单',     icon: 'gold-coin-o',     api: 'financePaymentsList',           detailType: 'payment',         statusParam: 'status',  sum: 'amount' },
  plans:         { title: '回款计划',   icon: 'gold-coin-o',     api: 'financePaymentPlansList',       detailType: 'plan',            statusParam: '',        sum: 'amount' },
  invoices:      { title: '开票',       icon: 'records',         api: 'financeInvoicesList',           detailType: 'invoice',         statusParam: 'status',  sum: 'amount' },
  refunds:       { title: '退款',       icon: 'cash-back-record',api: 'financeRefundsList',            detailType: 'refund',          statusParam: 'status',  sum: 'amount' },
  sellContracts: { title: '销售合同',   icon: 'orders-o',        api: 'financeSellOrdersList',         detailType: 'sellContract',    statusParam: 'stage',   sum: 'sales_amount' },
  finContracts:  { title: '财务合同',   icon: 'bill-o',          api: 'financeContractsList',          detailType: 'financeContract', statusParam: 'stage',   sum: 'amount' },
  room:          { title: '会议室订单', icon: 'wap-home-o',      api: 'financeRoomOrdersList',         detailType: 'room',            statusParam: 'is_free', sum: 'amount' },
  valueAdded:    { title: '增值订单',   icon: 'send-gift-o',     api: 'financeValueAddedOrdersList',   detailType: 'valueAdded',      statusParam: '',        sum: 'sales_amount' },
  purchase:      { title: '采购单',     icon: 'shopping-cart-o', api: 'financePurchaseContractsList',  detailType: 'purchase',        statusParam: 'stage',   sum: 'amount' }
}

/* 状态筛选标签：所有列表首项统一「全部」 */
const ALL = { label: '全部', value: '' }
const STATUS_TABS = {
  /* 【P207】消息：待我处理（默认 '' → 后端按 status=2）/ 已处理（3 通过 + 5 未通过） */
  /* 【P214】消息的两个 tab 直接对应 t_saas_data_message.is_action：0 待处理 / 1 已处理 */
  /* 【P244】消息三 Tab（与市场/销售/运营统一）：未读 / 待处理 / 全部 */
  msg:           [{ label: '未读', value: '2' }, { label: '待处理', value: '0' }, { label: '全部', value: '' }],
  todo:          [ALL],
  payments:      [ALL, { label: '审核中', value: 0 }, { label: '通过', value: 1 }, { label: '驳回', value: 2 }, { label: '待提交', value: 3 }],
  plans:         [ALL],
  invoices:      [ALL, { label: '审核中', value: 0 }, { label: '通过', value: 1 }, { label: '驳回', value: 2 }, { label: '待提交', value: 3 }],
  refunds:       [ALL, { label: '审核中', value: 0 }, { label: '通过', value: 1 }, { label: '驳回', value: 2 }, { label: '待提交', value: 3 }],
  sellContracts: [ALL, { label: '待执行', value: 1 }, { label: '已预定', value: 2 }, { label: '已支付', value: 3 }, { label: '已全款', value: 4 }],
  finContracts:  [ALL, { label: '待执行', value: 1 }, { label: '已订金', value: 2 }, { label: '已全款', value: 3 }, { label: '完成', value: 4 }],
  room:          [ALL, { label: '收费', value: 1 }, { label: '免费', value: 2 }],
  valueAdded:    [ALL],
  purchase:      [ALL, { label: '待执行', value: 1 }, { label: '已订金', value: 2 }, { label: '已全款', value: 3 }, { label: '完成', value: 4 }]
}

/* 高级筛选（服务端参数）：按单据类型给出可用维度。
   【P175】原则：只暴露「后端已实现 + DB 有真实区分度」的维度，避免出现「选了没反应」。

   【P179】用户反馈「7 个模块筛选都是用问题的」——根因取证结论：
     后端 8 个 List 方法的筛选**全部已实现且生效**（cs8 逐维度实测 total 均随筛选变化）；
     真正的缺陷在**前端暴露的维度太少**：refunds / sellContracts / finContracts /
     valueAdded 四类在高级筛选面板里**只有「日期」一项** ⇒ 用户点开是近乎空白的面板，
     观感即为「筛选没用」。
     故本次把后端已支持、且已验证有数据区分度的维度**全部暴露**（见下），
     并把「销售人员」提升为所有列表的**通用维度**（不再只在支付/开票/退款三处出现）。

   —— 仍刻意排除的退化维度（选了 total 恒等于全量，属数据层退化，勿加回）：
      · finContracts.payment_type → pro_contracts.payment_type 全库 19862 行全为 1（实测恒等）
      · finContracts.category     → category 19861 行为 1、仅 1 行为 4（实测 19861/19862，无区分度）
      · refunds.refundPayment_method → 全库仅 2 条且同值（实测 2/2，无区分度）
      · valueAdded.stage / status → 全库仅 5 条且全同值（实测 5/5，无区分度）
      · valueAdded.start_date     → 全库 5 条全空
      · purchase.*                → pro_contracts.is_purchase=1 全库 0 条（采购单数据尚未产生）
   日期区间为通用维度（date_from / date_to，YYYY-MM-DD），由下方 DATE_FIELD 自动注入。 */
const OPT = (arr) => arr.map(o => ({ text: o[1], value: o[0] }))
/* 【P187】旧「单组日期」保留（回款计划 / 销售合同 / 财务合同 / 采购单仍用它） */
const DATE_FIELD = { prop: 'dateRange', param: 'date_from', label: '日期', type: 'date' }

/* 【P187】关联单号选择器字段工厂（kind 对应后端 filterRefOptions 的 kind）
   值 = sn 字符串；下拉数据由选择器弹层按需拉取（可搜索） */
const REF_FIELD = (param, label, kind) => ({ prop: param + '_ref', param: param, label: label, type: 'ref', kind: kind })
/* 【P187】门店选择器（二级联动的一级） */
const STORE_FIELD = (label) => ({ prop: 'stores_sn_pick', param: 'stores_sn', label: label || '门店', type: 'store' })
/* 【P187】会议室选择器（二级联动的二级，依赖同面板的 stores_sn） */
const ROOM_FIELD = (label) => ({ prop: 'meeting_room_sn_pick', param: 'meeting_room_sn', label: label || '会议室', type: 'room', dependOn: 'stores_sn' })
/* 【P187】按列前缀命名的日期区间字段（一个列表可出现多组） */
const DATE_FIELD2 = (param, label) => ({ prop: param + '_range', param: param, label: label, type: 'date' })

/* 【P179】支付/开票/退款/会议室/增值 五类的「销售人员」维度由常驻选择器（fn2-owner）承担，
   advFields 内**不再重复**提供 owner_item，避免同一维度两处入口造成歧义。
   销售合同 / 财务合同 两张表按 owner_id 归属，同样由常驻选择器覆盖。 */

/* 单据类型（销售合同 = 订单合同）：0未定 / 1预定合同 / 2充值合同 */
const ORDER_TYPE_OPT = OPT([[0, '未定'], [1, '预定合同'], [2, '充值合同']])
/* 支付方式（与后端 self::PAY_METHOD 同口径） */
const PAY_METHOD_OPT = OPT([[1, '收钱吧'], [2, '网银'], [3, '快团团'], [4, '美团'], [5, '现金'], [6, '充值余额'], [7, '其他']])
/* 【P197】支付单「支付类型」—— 对应库表列 payment_type（列注释即「支付类型」，**不是**合同类型）。
   取值由 cs7 生产库 26200 行 name 样本反推：2=回款(26158) / 1=收款(35) / 4=其他收款(7)。
   ⚠️ P187~P191 曾误标为「合同类型」并套用业务类型字典（会议室/餐饮/茶歇/客户充值/其他服务），
      与真实取值域完全不符 ⇒ 用户选任何项都筛不到正确结果，P197 修正为后端 self::PAY_TYPE 同口径。 */
const PAY_TYPE_OPT = OPT([[1, '收款'], [2, '回款'], [4, '其他收款']])
/* 【P187】会议室订单「使用时段」（与后端 self::TIME_TYPE 同口径；表内真实有值的取值） */
const TIME_TYPE_OPT = OPT([[1, '上午'], [2, '下午'], [3, '晚上'], [4, '全天'], [5, '上午+下午'], [6, '小时'], [7, '下午+晚上']])

/* 【P419】RoomFilter → 财务端 room 列表的回写文案与取值换算
   · 时段：RoomFilter 与后端 self::ROOM_TIME 同口径（1全天 2白天 3上午 4下午 5晚上 6小时 7下午+晚上）。
     ⚠️ 本页 ADV_FIELDS.room 里旧的 TIME_TYPE_OPT 标注与此不一致（1 标成「上午」等），
        属历史标注错误；会议室订单改用 RoomFilter 后该字段不再被使用，故不动它。
   · 审核状态：RoomFilter 的 approval_tab 是**审批单状态**（2审核中/3通过/5驳回），
     财务端 room 列表筛的是本表 status 列（0审核中/1通过/2驳回）⇒ 必须换算，
     否则「选审核中」会把「驳回」的单筛出来。 */
const RT_TIME_LABEL = { 1: '全天', 2: '白天', 3: '上午', 4: '下午', 5: '晚上', 6: '小时', 7: '下午+晚上' }
const RT_APPROVAL_TO_STATUS = { '2': 0, '3': 1, '5': 2 }
const RT_STATUS_TO_APPROVAL = { 0: '2', 1: '3', 2: '5' }
const RT_STATUS_LABEL = { 0: '审核中', 1: '通过', 2: '驳回' }

/* 【P191】四态审核状态（与后端 self::PAY_STATUS 同口径）
   支付单 / 开票 / 退款 三类的顶部标签行与高级筛选面板共用此取值域，
   param 同为 `status` ⇒ 两处入口效果一致，不会互相矛盾。 */
const AUDIT_STATUS_OPT = OPT([[0, '审核中'], [1, '通过'], [2, '驳回'], [3, '待提交']])
/* 【P191】会议室订单审核状态（后端 status 仅三态：0审核中/1通过/2驳回） */
const ROOM_STATUS_OPT = OPT([[0, '审核中'], [1, '通过'], [2, '驳回']])
/* 【P191】增值订单 stage（后端 self::ORDER_STAGE 口径） */
const VA_STAGE_OPT = OPT([[1, '待执行'], [2, '已订金'], [3, '已全款'], [4, '完成']])
/* 【P191】财务合同业务类型 / 支付单合同类型（同字典） */
const BIZ_TYPE_OPT = OPT([[1, '会议室'], [2, '餐饮'], [3, '茶歇'], [4, '客户充值'], [5, '其他服务']])
/* 【P200】退款类型（退款表列 paymente_method：1 非余额支付 2 余额支付）
   —— P197 曾以 contracts_type（全库 2/2 恒 1）裁定跳过，本轮改用 paymente_method。 */
const REFUND_TYPE_OPT = OPT([[1, '非余额支付'], [2, '余额支付']])
/* 【P200】增值「订单类型」—— 后端 JOIN t_pro_orders.type（增值表自身 pay_type 全库 NULL）
   取值：1 普通订单 / 2 充值订单（另含 0 未定，库内 874 条但增值表样本未命中） */
const VA_ORDER_TYPE_OPT = OPT([[1, '普通订单'], [2, '充值订单']])

/* ============================================================
   【P187 + P191 + P197 + P200】财务端 5 个列表「筛选条件」全套改造
   ------------------------------------------------------------
   用户需求原文（P187 首次下达 → P191 复述细化 → P197 三度下达 → P200 四度下达）：
     · 会议室订单：客户 / 商机 / 销售合同 / 财务合同 / 使用日期（区间）/
                    使用时段 / 门店 / 会议室（★ 二级联动）/ 创建日期（区间）/
                    负责人 / 状态 + 是否显示合计金额
     · 支付单：    客户 / 商机 / 销售合同 / 财务合同 / 回款日期（区间）/
                    创建日期（区间）/ 支付方式 / 合同类型 / 负责人 / 状态
                    + 是否显示合计金额
     · 开票：      客户 / 销售合同 / 财务合同 / 开票日期（区间）/
                    创建日期（区间）/ 开票类型 / 负责人 / 状态 + 是否显示合计金额
     · 退款：      客户 / 商机 / 销售合同 / 财务合同 / 创建日期（区间）/
                    退款类型 / 负责人 / 状态 + 是否显示合计金额
     · 增值：      客户 / 商机 / 销售合同 / 财务合同 / 创建日期（区间）/
                    订单类型 / 负责人 / 状态 + 是否显示合计金额

   ★★★ P200 可实现性裁定（用户授权「做不到的跳过」；依据 = cs7 生产库实测 + 接口实测）
     ⚠️ 前提：cs8 库（liubai_crm_cs）**全部财务业务表 0 行**，所有区分度验证必须在
        cs7 生产库（liubai_crm）做。累计行数：支付单 26202 / 开票 11988 /
        退款 2 / 增值 5 / 会议室订单 4794 / 合同 19862。
     · ✅ 会议室订单 —— 清单 11 项**全部已实现**（含 门店↔会议室 二级联动、
        使用时段 time_type 实测 1=1519/2=1382/4=669/5=524/3=421/7=279 有区分度）。
     · ⚠️ 支付单「合同类型」—— **本轮再次跳过**（用户已二次确认）。硬证据：
        ① 性能不可行：t_pro_orders_payment(26202 行) 与 t_pro_contracts(19862 行)
           **均只有主键一个索引**，跨表 EXISTS 实测 **49.9 秒**、IN 子查询 **29.6 秒**，
           远超前端超时（列表接口必须秒级返回）；
        ② 区分度为零：三个候选列全库各自恒为单一值 ——
           t_pro_contracts.payment_type 19862/19862=1、
           category 19861/19861=1、type 19862/19862=1，
           即使加索引也筛不出任何有意义结果。
        ⇒ 保留已有的「支付类型」（payment_type：收款/回款/其他收款）作为替代维度。
     · ✅ 开票 —— 清单 7 项全部已实现（客/销/财/开票日期/创建日期/开票类型/状态）。
        用户清单**未含「商机」**（与实测一致：t_pro_invoices 无 opportunitie_sn 列，
        实测 information_schema 命中数 = 0；强加会出现「选了没反应」）。
     · ✅ 退款「退款类型」—— **P200 新增，已实现**。源列 = paymente_method
        （注释「支付类型 1非余额支付 2余额支付」）。P197 曾以 contracts_type
        （全库 2/2 恒 1）裁定跳过，本轮改用 paymente_method ⇒ 语义成立。
        实测：退款全量 2 → 筛「非余额支付」命中 2（随数据增长会体现区分度）。
        ⚠️ 与既有「退款方式」（refundPayment_method）是**两个不同列**，并存不冲突。
     · ✅ 增值「订单类型」—— **P200 新增，已实现**。走**跨表派生**：
        后端 JOIN t_pro_orders.type（列注释「1普通订单 2充值订单」），
        因增值表自身 pay_type / option_service **全库 NULL**（P197 判据）。
        实测：增值全量 5 → 筛「普通订单」命中 3、「充值订单」命中 0，**有区分度**。
        性能：增值表仅 5 行，IN 子查询实测 11ms，可安全启用。
        ⚠️ 跨表筛选仅对小表可用；大表（支付单）已按上述①一律禁用。

   【P191 口径裁定 · 仍然有效】
     · 退款 / 增值 的「开票日期」→ 按**笔误**处理：这两张表无任何开票列，
       退款沿用「创建日期」；增值额外补「使用日期」区间（用户清单漏写）。
     · 存量维度**全部保留**：支付单「产品类型 / 付款状态」、增值「使用日期 /
       门店 / 阶段」、退款「退款方式」——均为已上线维度（部分数据退化，见文末）。
     · 「回款支付 → 支付单」已核对：底部 Tab 与快捷入口均为「支付单」（P116 已改完），
       源码仅剩注释字样，本轮顺手同步，无用户可见遗留。

   【P197 合计金额 · P200 增补双入口】5 类列表**全部支持**：
     · 常量 SUM_LIST_KEYS = { room, payments, invoices, refunds, valueAdded }；
       localStorage 前缀 fn_sum_on_<key>；**默认关闭**；开关 .fn2-sum__toggle。
     · 口径 = **当前筛选条件**下全量合计（服务端 listSum），非当前页合计。
     · 已按用户要求**隐藏列表内合计金额条**：<bill-list> 必传 :show-amount="false"。
     · 采购单 / 销售合同 / 财务合同 / 回款计划**不提供**合计（无 sum 声明）。
     · 【P200】用户要求把「是否显示合计金额」**列进筛选清单** ⇒ 在高级筛选面板
       底部**新增同名开关行**（.fn2-adv--switch），与列表头的开关**共用同一状态**
       （sumOn[curKey]）与同一 localStorage 键 ⇒ 两处入口双向同步、不会矛盾。
       ★ 设计口径：该开关**不参与 where**（合计不是筛选项），只控制列表头右侧
         合计数字的显隐；数字本身仍严格 = 服务端「当前筛选条件下全量合计」。

   实现口径（与后端 _fltSpec 一一对应，改这里必须同步改后端）：
     · 关联单号 param：customer_sn / opportunitie_sn / parent_sn（销售合同）/
                       contracts_sn（财务合同）
     · 日期区间 param：<col>_from / <col>_to，col ∈
                       on_site / create / start（会议室、增值）
                       payment（支付单）/ signing（开票）/ create（退款、开票、支付）
     · 门店 / 会议室：stores_sn / meeting_room_sn
     · 负责人：owner_id —— 由**常驻选择器**（fn2-owner）承担，见本常量下方说明，
       故不在 advFields 内重复暴露（避免同一参数两处入口造成歧义）。
     · 支付类型（P197 新增回显）：payment_type + 后端 self::PAY_TYPE 同字典。
     · 【P200】退款类型：paymente_method（本表列，后端 self::REFUND_TYPE）。
     · 【P200】增值订单类型：order_type —— **跨表派生**，后端 `_fltJoin` 生成
       `order_sn IN (SELECT sn FROM t_pro_orders WHERE type IN (...))`。
       ⚠️ 跨表筛选只在**小表**启用（增值 5 行 / 11ms）；大表全部禁用（性能原因见上）。

   ⚠️ 与「顶部标签」同名的维度**分两种情形**：
       (a) 顶部标签已覆盖同一语义、且后端顶部参数与高级筛选**共用同一 param** 的
           → 不在 advFields 重复暴露（会议室订单 is_free「收费/免费」）；
       (b) 顶部标签虽在、但用户明确要求把它列进筛选清单的
           → **照做**：支付单 / 开票 / 退款 的 status 已补入 advFields。
           实现要点：两者 param 同名 `status`，取值域相同，
           由 `_applyFlt` 统一落 where，**不会产生双入口矛盾**
           （用户在面板里选与在标签行选，效果一致，且 statusKey 会同步回显）。

   【已知数据退化维度】（保留展示，但选后可能无区分效果 —— 非代码缺陷）
     实测来源 cs7 生产库：
       · refunds.refundPayment_method → 2/2 同值（表内仅 2 条）
       · refunds.paymente_method      → 2/2 同为 1（P200 新增维度，数据量少所致）
       · refunds.contracts_type       → 2/2 全为 1（已裁定不暴露）
       · valueAdded.stage / status    → 5/5 同值；pay_type / option_service 5/5 全 NULL
       · valueAdded.start_date        → 5/5 全空
       · purchase.*                   → pro_contracts.is_purchase=1 全库 0 条（数据未产生）
       · contracts.payment_type / category / type → 全库各自恒为单一值（故「合同类型」跳过） */
const ADV_FIELDS = {
  /* 【P207】消息：业务类型 + 提交时间（提交人由常驻选择器 fn2-owner 承担，
     与其余列表口径一致：同一维度不设两处入口） */
  msg: [
    /* 【P214】消息数据源改为 t_saas_data_message 后，原「业务类型」维度（依赖审批单 biz 字段）
       已不适用；改为「消息类型」（type 0 通知 / 1 审批）。 */
    { prop: 'type', param: 'type', label: '消息类型',
      options: OPT([[0, '通知'], [1, '审批']]) },
    DATE_FIELD2('create', '发送时间')
  ],
  payments: [
    REF_FIELD('customer_sn', '客户', 'customer'),
    REF_FIELD('opportunitie_sn', '商机', 'opportunity'),
    REF_FIELD('parent_sn', '销售合同', 'sales_contract'),
    REF_FIELD('contracts_sn', '财务合同', 'finance_contract'),
    DATE_FIELD2('payment', '回款日期'),
    DATE_FIELD2('create', '创建日期'),
    { prop: 'payment_method', param: 'payment_method', label: '支付方式', options: PAY_METHOD_OPT },
    /* 【P197 修正】原为「合同类型」+ BIZ_TYPE_OPT（错误），见 PAY_TYPE_OPT 上方说明。
       库表列 = payment_type，语义 = 支付类型，与后端 self::PAY_TYPE 同口径。 */
    { prop: 'payment_type', param: 'payment_type', label: '支付类型', options: PAY_TYPE_OPT },
    { prop: 'payment_product', param: 'payment_product', label: '产品类型', options: BIZ_TYPE_OPT },
    { prop: 'is_payment', param: 'is_payment', label: '付款状态',
      options: OPT([[1, '已付款'], [0, '未付款']]) },
    /* 【P191】补「状态」：用户清单明确要求把状态列进筛选维度。
       与顶部标签行共用 param `status`（取值域一致），后端 paymentsList 已实现。 */
    { prop: 'pay_status', param: 'status', label: '状态', options: AUDIT_STATUS_OPT }
  ],
  plans: [DATE_FIELD],
  invoices: [
    REF_FIELD('customer_sn', '客户', 'customer'),
    /* ⚠️ 开票表列名是 order_sn（不是 parent_sn）；后端 _fltSpec 已做映射 */
    REF_FIELD('parent_sn', '销售合同', 'sales_contract'),
    REF_FIELD('contracts_sn', '财务合同', 'finance_contract'),
    DATE_FIELD2('signing', '开票日期'),
    DATE_FIELD2('create', '创建日期'),
    { prop: 'invoice_type', param: 'invoice_type', label: '开票类型',
      options: OPT([[1, '增值税专用发票'], [2, '增值税普通发票']]) },
    /* 【P191】补「状态」：与顶部标签行共用 param `status`。（「商机」不做：
       t_pro_invoices 无 opportunitie_sn 列，暴露会出现「选了没反应」。） */
    { prop: 'inv_status', param: 'status', label: '状态', options: AUDIT_STATUS_OPT }
  ],
  refunds: [
    REF_FIELD('customer_sn', '客户', 'customer'),
    REF_FIELD('opportunitie_sn', '商机', 'opportunity'),
    REF_FIELD('parent_sn', '销售合同', 'sales_contract'),
    /* ⚠️ 本表 contracts_sn 有列但实测无填充 ⇒ 选了会筛出空集，属数据现状非缺陷 */
    REF_FIELD('contracts_sn', '财务合同', 'finance_contract'),
    DATE_FIELD2('create', '创建日期'),
    { prop: 'refundPayment_method', param: 'refundPayment_method', label: '退款方式',
      options: OPT([[1, '退到余额'], [2, '人工退款'], [3, '转到其他订单']]) },
    /* 【P200】新增「退款类型」= 库表列 paymente_method（注释「1非余额支付 2余额支付」）。
       ⚠️ 与上方「退款方式」（refundPayment_method）**不是同一列**，两者并存不冲突。 */
    { prop: 'refund_type', param: 'paymente_method', label: '退款类型', options: REFUND_TYPE_OPT },
    /* 【P191】补「状态」。⚠️「开票日期」按笔误处理：退款表无任何开票列，
       沿用上方「创建日期」即可（用户已确认）。 */
    { prop: 'ref_status', param: 'status', label: '状态', options: AUDIT_STATUS_OPT }
  ],
  sellContracts: [
    { prop: 'order_type', param: 'type', label: '单据类型', options: ORDER_TYPE_OPT },
    DATE_FIELD
  ],
  finContracts: [
    { prop: 'fin_category', param: 'category', label: '业务类型',
      options: OPT([[1, '会议室'], [2, '餐饮'], [3, '茶歇'], [4, '客户充值'], [5, '其他服务']]) },
    DATE_FIELD
  ],
  room: [
    REF_FIELD('customer_sn', '客户', 'customer'),
    REF_FIELD('opportunitie_sn', '商机', 'opportunity'),
    REF_FIELD('parent_sn', '销售合同', 'sales_contract'),
    REF_FIELD('contracts_sn', '财务合同', 'finance_contract'),
    DATE_FIELD2('on_site', '使用日期'),
    DATE_FIELD2('create', '创建日期'),
    { prop: 'time_type', param: 'time_type', label: '使用时段', options: TIME_TYPE_OPT },
    /* ★ 门店 ↔ 会议室 二级联动：先选门店，会议室列表随之收敛 */
    STORE_FIELD('门店'),
    ROOM_FIELD('会议室'),
    /* 会议室订单顶部标签是「收费/免费」（is_free），故此处只补「审核状态」 */
    { prop: 'room_status', param: 'status', label: '审核状态', options: ROOM_STATUS_OPT }
  ],
  valueAdded: [
    REF_FIELD('customer_sn', '客户', 'customer'),
    REF_FIELD('opportunitie_sn', '商机', 'opportunity'),
    REF_FIELD('parent_sn', '销售合同', 'sales_contract'),
    /* ⚠️ 增值表无 contracts_sn 列 ⇒ 后端把「财务合同」映射到 order_sn */
    REF_FIELD('contracts_sn', '财务合同', 'finance_contract'),
    DATE_FIELD2('on_site', '使用日期'),
    DATE_FIELD2('create', '创建日期'),
    STORE_FIELD('门店'),
    /* 【P200】新增「订单类型」= 后端 JOIN t_pro_orders.type（1普通订单/2充值订单）。
       增值表自身 pay_type 全库 NULL、option_service 全库 NULL，二者均不可用。 */
    { prop: 'va_order_type', param: 'order_type', label: '订单类型', options: VA_ORDER_TYPE_OPT },
    { prop: 'va_stage', param: 'stage', label: '阶段', options: VA_STAGE_OPT },
    { prop: 'va_status', param: 'status', label: '状态', options: ROOM_STATUS_OPT }
  ],
  purchase: [DATE_FIELD]
}

/* 各模块「日期」维度对应的库表列名（仅用于筛选条文案说明，不参与请求） */
const DATE_COL = {
  payments: 'payment_date', invoices: 'signing_date', refunds: 'create_time',
  sellContracts: 'signing_date', finContracts: 'signing_date',
  room: 'on_site_time', valueAdded: 'start_date', purchase: 'signing_date'
}

/* 【P187】支持「服务端全量合计」的列表 key（与后端 FinanceController::_listSpecMap 一一对应）
   ⚠️ 仅这 5 类有 listSum 支持；其余（回款计划/销售合同/财务合同/采购单/消息/待办）
      后端未提供合计，前端不显示开关。 */
const SUM_LIST_KEYS = { room: 1, payments: 1, invoices: 1, refunds: 1, valueAdded: 1 }
/* 【P187】合计开关的 localStorage 键（按列表 key 分别记忆） */
const SUM_LS_PREFIX = 'fn_sum_on_'

/* 【P202】排序文案（头部按钮短文案 + 面板「当前：」长文案）
   未命中（含 sortKey='' 的默认排序）由调用处兜底：按钮「排序」、面板「默认排序（最新在前）」 */
const SORT_BTN_TEXT = {
  'amount-desc': '金额 ↓', 'amount-asc': '金额 ↑',
  'date-desc': '日期 ↓', 'date-asc': '日期 ↑'
}
const SORT_FULL_TEXT = {
  'amount-desc': '金额从高到低', 'amount-asc': '金额从低到高',
  'date-desc': '日期从新到旧', 'date-asc': '日期从旧到新'
}

/* 审批业务类型 → 单据详情类型（待办详情里联动展示关联单据） */
const BIZ_TO_TYPE = { payment: 'payment', refund: 'refund', room: 'room' }

const STATUS_CLASS = {
  '通过': 'tag-receive', '驳回': 'tag-danger', '审核中': 'tag-wait', '待提交': 'tag-allot',
  '等待审核': 'tag-wait', '未开始': 'tag-allot', '执行中': 'tag-wait', '待完善': 'tag-wait',
  '未通过': 'tag-danger', '拒绝': 'tag-danger',
  '待执行': 'tag-allot', '已订金': 'tag-wait', '已全款': 'tag-receive', '完成': 'tag-receive',
  '已预定': 'tag-wait', '已支付': 'tag-receive', '未定': 'tag-charge',
  '免费': 'tag-free', '收费': 'tag-charge'
}

function money(v) {
  const n = Number(v || 0)
  return '¥' + n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
/* 【P184】销售端金额写法：`¥` + 原值直接拼接（不补小数、不加千分位），如 ¥500 / ¥4,800 */
function moneyRaw(v) {
  if (v === null || v === undefined || v === '') { return '' }
  return '¥' + v
}
/* 【P184】销售合同/财务合同「审核状态」文案：财务端接口未下发 status_name，
   但 status 列有值，语义与销售端 getOrdersListData().item.status 一致。
   0=审核中 / 1=审核通过 / 2=审核驳回（销售端 client.vue L1098 同口径）。 */
const AUDIT_TEXT = { 0: '审核中', 1: '审核通过', 2: '审核驳回' }
function auditText(st) {
  if (st === null || st === undefined || st === '') { return '' }
  return AUDIT_TEXT[Number(st)] || ''
}
/* 【P184】审核状态标签配色：0 橙 / 2 红 / 1 绿（对齐销售端 contractStatusClass） */
function auditClass(st) {
  const n = Number(st)
  if (n === 0) { return 'tag-wait' }
  if (n === 2) { return 'tag-danger' }
  if (n === 1) { return 'tag-receive' }
  return 'tag-allot'
}
/* 【P184】会议室订单审核状态文案：0=审核中 / 1=通过 / 2=驳回 */
const ROOM_AUDIT_TEXT = { 0: '审核中', 1: '通过', 2: '驳回' }
/* 【P184】时间段文案：财务端 roomOrdersList **不下发 time_type_text**（销售端有），
   但下发 time_type，故前端本地映射，保持与销售端 header 的 role 一致。
   口径见后端注释：1全天 / 2白天 / 3上午 / 4下午 / 5晚上 / 6小时 / 7下午+晚上 */
const ROOM_TIME_TEXT = { 1: '全天', 2: '白天', 3: '上午', 4: '下午', 5: '晚上', 6: '小时', 7: '下午+晚上' }
/* 【P184】会议室订单审核状态配色：0 橙 / 2 红 / 1 绿（对齐销售端 roomOrderApprovalClass） */
function roomAuditClass(st) {
  const n = Number(st)
  if (n === 0) { return 'tag-wait' }
  if (n === 2) { return 'tag-danger' }
  return 'tag-receive'
}
function s(v) {
  return (v === null || v === undefined || v === '') ? '' : String(v)
}
function cut(v, n) {
  const t = s(v)
  return t.length > n ? t.slice(0, n) + '…' : t
}
function num(v) {
  if (v === null || v === undefined || v === '') { return null }
  const n = Number(v)
  return isNaN(n) ? null : n
}

/* 卡片行适配器：key → rows[] 的每一行
   _amountNum / _date 供排序用（与销售端的 amount-desc / date-desc 口径一致）

   【P184】★ 结构对齐销售端 client.vue（用户需求：「有列表的列需要跟销售端一样」）：
     销售端卡片 = header(name + role + 状态标签)
                → 标签行(.item-flags：收费/免费 + 审核状态)
                → 正文行(_row1：客户 · 编号)
                → 日期行(row--dates：签约 / 起 / 止)
                → 金额行(.customer-card__amount：标签 + 值)
     财务端此前把「金额」挤进 header 的 role 小标签、且缺「审核状态行」「起/止日期行」，
     本次逐项补齐。role 恢复为销售端语义（合同类型 / 时间段 / 产品类型）。 */
const ADAPTERS = {
  todo: r => {
    const bs = r.biz_summary || {}
    /* 【P220】补 invoice：此前只映射 payment/refund/room，发票审批单恒落到「其他单据」，
       且 refName 也不读 biz_summary.invoice_name ⇒ 「单据：」行退化成关联单号。 */
    const bizName = { payment: '支付单', refund: '退款单', room: '会议室订单', invoice: '发票' }[r.biz] || '其他单据'
    const refName = bs.payment_name || bs.refund_name || bs.room_order_name || bs.invoice_name || bs.invoice_no || ''
    return {
      _title: s(r.task_descript) || '审批单',
      _role: bizName,
      _badge: bizName,
      _amount: num(bs.amount) != null ? money(bs.amount) : '',
      _amountLabel: num(bs.amount) != null ? '金额' : '',
      _amountValue: num(bs.amount) != null ? moneyRaw(bs.amount) : '',
      _amountNum: num(bs.amount),
      _date: s(r.create_time),
      _status: s(r.status_name) || '等待审核',
      _statusClass: STATUS_CLASS[s(r.status_name)] || 'tag-wait',
      _flagTags: [],
      _row1: refName ? ('单据：' + cut(refName, 26)) : ('关联单号：' + cut(r.parent_sn, 26)),
      _row2: '提交：' + s(r.create_time) + (r.create_user_name ? ' · ' + r.create_user_name : '')
    }
  },
  /* 【P296】★★ 修 `msg: null`
     原注释说「消息与待办行结构一致 ⇒ 共用同一适配器」，但下一行写的是 `msg: null`。
     而 onLoad 里取适配器是 `ADAPTERS[this.curKey] || (x => x)` ——
     **null 是 falsy，会退化成恒等函数**，于是消息卡片拿不到任何 _title/_row1/_status，
     渲染成几乎空白的白条 ⇒ ① 视觉上是「空列表」；② 内容高度撑不起来 ⇒ 无下拉空间。
     这里按消息表（t_saas_data_message）真实字段补一个适配器：
       title←content、role←type（0 通知 / 1 审批）、status←is_read+is_action 两态。 */
  msg: r => ({
    _title: s(r.content) || s(r.title) || '消息',
    _role: Number(r.type) === 1 ? '审批' : '通知',
    _badge: Number(r.type) === 1 ? '审批' : '通知',
    _amount: '',
    _amountLabel: '',
    _amountValue: '',
    _amountNum: null,
    _date: s(r.create_time),
    /* 状态标签：待处理(红) / 未读(蓝) / 已处理(灰)，与三 Tab 口径一致 */
    _status: Number(r.is_action) === 0 ? '待处理' : (String(r.is_read) === '1' ? '已读' : '已处理'),
    _statusClass: Number(r.is_action) === 0 ? 'tag-wait' : 'tag-receive',
    _flagTags: [],
    _row1: s(r.parent_sn) ? ('单号：' + cut(r.parent_sn, 26)) : '',
    _row2: '时间：' + (s(r.create_time) || '-')
  }),

  payments: r => ({
    _title: s(r.name) || s(r.title) || s(r.serial_number) || '支付单',
    /* role = 产品类型（销售端会议室订单用「时间段」，支付单等价位用产品类型） */
    _role: s(r.product_name),
    _badge: s(r.product_name),
    _amount: money(r.amount),
    _amountLabel: '支付金额',
    _amountValue: moneyRaw(r.amount),
    _amountNum: num(r.amount),
    _date: s(r.payment_date) || s(r.create_time),
    _status: s(r.status_name),
    _statusClass: STATUS_CLASS[s(r.status_name)] || 'tag-wait',
    _flagTags: [],
    _row1: '客户：' + (s(r.customer_name) || '-') + (r.parent_name ? ' · 合同：' + cut(r.parent_name, 20) : ''),
    _row2: '支付方式：' + (s(r.payment_method_name) || '-')
      + (r.payment_type_name ? ' · 支付类型：' + s(r.payment_type_name) : '')
      + ' · ' + s(r.payment_date)
      + (r.create_user_name ? ' · ' + r.create_user_name : '')
  }),
  plans: r => ({
    _title: s(r.name) || '回款计划',
    _role: s(r.stores_name),
    _badge: s(r.stores_name),
    _amount: money(r.amount),
    _amountLabel: '回款金额',
    _amountValue: moneyRaw(r.amount),
    _amountNum: num(r.amount),
    _date: s(r.payment_date) || s(r.create_time),
    _status: s(r.payment_date) ? '回款日 ' + s(r.payment_date) : '',
    _statusClass: 'tag-allot',
    _flagTags: [],
    _row1: '客户：' + (s(r.customer_name) || '-') + (r.order_name ? ' · 合同：' + cut(r.order_name, 20) : ''),
    _row2: '会议室：' + (s(r.meeting_room_name) || '-') + (r.create_user_name ? ' · ' + r.create_user_name : '')
  }),
  invoices: r => ({
    _title: s(r.name) || s(r.title) || '发票',
    _role: s(r.type_name),
    _badge: s(r.type_name),
    _amount: money(r.amount),
    _amountLabel: '开票金额',
    _amountValue: moneyRaw(r.amount),
    _amountNum: num(r.amount),
    _date: s(r.signing_date) || s(r.create_time),
    _status: s(r.status_name),
    _statusClass: STATUS_CLASS[s(r.status_name)] || 'tag-wait',
    _flagTags: [],
    _row1: '客户：' + (s(r.customer_name) || '-') + (r.contracts_name ? ' · 合同：' + cut(r.contracts_name, 18) : ''),
    _row2: '开票日期：' + (s(r.signing_date) || '-') + (r.create_user_name ? ' · ' + r.create_user_name : '')
  }),
  refunds: r => ({
    _title: s(r.name) || s(r.title) || '退款单',
    _role: s(r.refund_method_name),
    _badge: s(r.refund_method_name),
    _amount: money(r.amount),
    _amountLabel: '退款金额',
    _amountValue: moneyRaw(r.amount),
    _amountNum: num(r.amount),
    _date: s(r.create_time),
    _status: s(r.status_name),
    _statusClass: STATUS_CLASS[s(r.status_name)] || 'tag-wait',
    _flagTags: [],
    _row1: '客户：' + (s(r.customer_name) || '-') + (r.contracts_name ? ' · 合同：' + cut(r.contracts_name, 18) : ''),
    _row2: '创建：' + s(r.create_time) + (r.create_user_name ? ' · ' + r.create_user_name : '')
  }),
  /* 【P184】销售合同：对齐销售端 4 行 —— 审核状态标签 / 客户·编号 / 签约·起·止 / 销售金额 */
  sellContracts: r => ({
    _title: s(r.name) || s(r.title) || '销售合同',
    _role: s(r.type_name),
    _badge: s(r.type_name),
    _amount: money(num(r.sales_amount) != null ? r.sales_amount : r.total_amount),
    _amountLabel: '销售金额',
    _amountValue: moneyRaw(num(r.sales_amount) != null ? r.sales_amount : r.total_amount),
    _amountNum: num(r.sales_amount) != null ? num(r.sales_amount) : num(r.total_amount),
    _date: s(r.signing_date) || s(r.create_time),
    _status: s(r.stage_name),
    _statusClass: STATUS_CLASS[s(r.stage_name)] || 'tag-allot',
    /* 审核状态独立标签行（销售端 client.vue L1098-1100 同构） */
    _flagTags: auditText(r.status) ? [{ text: auditText(r.status), cls: auditClass(r.status) }] : [],
    _row1: '客户：' + (s(r.customer_name) || '-') + ' · 编号：' + (s(r.serial_number) || '-'),
    _dates: [
      '签约：' + (s(r.signing_date) || '-'),
      '起：' + (s(r.start_date) || '-'),
      '止：' + (s(r.end_date) || '-')
    ],
    _row2: '签约：' + (s(r.signing_date) || '-') + (r.owner_name ? ' · 归属：' + r.owner_name : '')
  }),
  /* 【P184】财务合同：同上，业务类型标签走 role */
  finContracts: r => ({
    _title: s(r.name) || s(r.title) || '财务合同',
    _role: s(r.category_name),
    _badge: s(r.category_name),
    _amount: money(r.amount),
    _amountLabel: '合同金额',
    _amountValue: moneyRaw(r.amount),
    _amountNum: num(r.amount),
    _date: s(r.signing_date) || s(r.create_time),
    _status: s(r.stage_name),
    _statusClass: STATUS_CLASS[s(r.stage_name)] || 'tag-wait',
    _flagTags: auditText(r.status) ? [{ text: auditText(r.status), cls: auditClass(r.status) }] : [],
    _row1: '客户：' + (s(r.customer_name) || '-') + ' · 编号：' + (s(r.serial_number) || '-'),
    _dates: [
      '签约：' + (s(r.signing_date) || '-'),
      '起：' + (s(r.start_date) || '-'),
      '止：' + (s(r.end_date) || '-')
    ],
    _row2: '签约：' + (s(r.signing_date) || '-') + (r.create_user_name ? ' · ' + r.create_user_name : '')
  }),
  /* 【P184】会议室订单：严格对齐销售端 roomorder 卡片 ——
       ★ 销售端会议室订单卡片**没有独立金额行**（金额已含在末行「销售价：xxx」里），
         故此处 `_amountLabel` 留空 ⇒ 不渲染 __amount 行，保持逐行 1:1。
       header: name + 时间段(time_type_text) + 审核状态标签
       标签行: 收费/免费 + 审核状态（对齐销售端 roomorder-flags）
       行1: 客户 · 门店
       行2: 会议室 · 销售价 · 市场价 */
  room: r => ({
    _title: s(r.name) || s(r.title) || '会议室订单',
    /* 【P202】服务端已下发 time_type_text / status_name（P184 遗留清理），
       优先消费；本地映射仅作老接口兼容兜底，不再作为主路径 */
    _role: s(r.time_type_text) || ROOM_TIME_TEXT[Number(r.time_type)] || '',
    _badge: Number(r.is_free) === 2 ? '免费' : '收费',
    _amount: money(r.amount),
    _amountLabel: '',
    _amountValue: '',
    _amountNum: num(r.amount),
    _date: s(r.on_site_time) || s(r.create_time),
    _status: s(r.status_name) || ROOM_AUDIT_TEXT[Number(r.status)] || '',
    _statusClass: roomAuditClass(r.status),
    _flagTags: [
      { text: Number(r.is_free) === 2 ? '免费' : '收费', cls: Number(r.is_free) === 2 ? 'tag-free' : 'tag-charge' },
      ...((s(r.status_name) || ROOM_AUDIT_TEXT[Number(r.status)])
        ? [{ text: s(r.status_name) || ROOM_AUDIT_TEXT[Number(r.status)], cls: roomAuditClass(r.status) }] : [])
    ],
    _row1: '客户：' + (s(r.customer_name) || '-') + ' · 门店：' + (s(r.stores_name) || '-'),
    _row2: '会议室：' + (s(r.meeting_room_name) || '-') + ' · 销售价：' + (r.amount != null ? r.amount : '-')
      + ' · 市场价：' + (r.price != null ? r.price : '-')
  }),
  valueAdded: r => ({
    _title: s(r.name) || s(r.title) || '增值订单',
    _role: s(r.stores_name),
    _badge: s(r.stores_name),
    _amount: money(r.sales_amount),
    _amountLabel: '销售金额',
    _amountValue: moneyRaw(r.sales_amount),
    _amountNum: num(r.sales_amount),
    _date: s(r.create_time),
    /* 【P202】服务端已下发 status_name / stage_name（P184 遗留清理） */
    _status: s(r.status_name) || s(r.stage_name),
    _statusClass: STATUS_CLASS[s(r.status_name) || s(r.stage_name)] || 'tag-wait',
    _flagTags: s(r.status_name) ? [{ text: s(r.status_name), cls: STATUS_CLASS[s(r.status_name)] || 'tag-wait' }] : [],
    _row1: '客户：' + (s(r.customer_name) || '-') + (r.order_name ? ' · 合同：' + cut(r.order_name, 18) : ''),
    _row2: '创建：' + s(r.create_time) + (r.create_user_name ? ' · ' + r.create_user_name : '')
  }),
  purchase: r => ({
    _title: s(r.name) || s(r.title) || '采购单',
    _role: s(r.category_name),
    _badge: s(r.category_name),
    _amount: money(r.amount),
    _amountLabel: '采购金额',
    _amountValue: moneyRaw(r.amount),
    _amountNum: num(r.amount),
    _date: s(r.signing_date) || s(r.create_time),
    _status: s(r.stage_name),
    _statusClass: STATUS_CLASS[s(r.stage_name)] || 'tag-wait',
    _flagTags: auditText(r.status) ? [{ text: auditText(r.status), cls: auditClass(r.status) }] : [],
    _row1: '供应商/客户：' + (s(r.customer_name) || '-') + ' · ' + (s(r.serial_number) || '-'),
    _dates: [
      '签约：' + (s(r.signing_date) || '-'),
      '起：' + (s(r.start_date) || '-'),
      '止：' + (s(r.end_date) || '-')
    ],
    _row2: '签约：' + (s(r.signing_date) || '-') + (r.create_user_name ? ' · ' + r.create_user_name : '')
  })
}
/* 【P214】消息适配器：数据源已改为 t_saas_data_message，
   字段与审批单不同（无 task_descript / biz_summary，改用 content / biz_name），
   故不能继续复用 ADAPTERS.todo。 */
ADAPTERS.msg = r => ({
  _title: s(r.content) || '消息',
  _role: s(r.type_name) || '通知',
  _badge: s(r.type_name) || '通知',
  _amount: '',
  _amountLabel: '',
  _amountValue: '',
  _amountNum: null,
  _date: s(r.create_time),
  _status: s(r.is_action_name) || '待处理',
  _statusClass: Number(r.is_action) === 1 ? 'tag-done' : 'tag-wait',
  _flagTags: [],
  _row1: r.biz_name ? ('单据：' + cut(r.biz_name, 26)) : ('关联单号：' + cut(r.parent_sn, 26)),
  _row2: '发送：' + s(r.create_time) + (r.create_user_name ? ' · ' + r.create_user_name : '')
})

/* 说明：图标由 v-html 注入，拿不到 scoped 的 data-v 属性，
   故尺寸/描边/填充一律以「内联表现属性」写在 <svg> 根上（currentColor 继承本类 color）。 */
function svg(inner, size) {
  const n = size || 23
  return '<svg viewBox="0 0 24 24" width="' + n + '" height="' + n + '" fill="none" stroke="currentColor"'
    + ' stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' + inner + '</svg>'
}

export default {
  name: 'FinanceHome',
  components: { BillList, RoomFilter, RoomCard, MessagePanel },
  data() {
    return {
      /* 底栏 */
      tabIndex: 0,
      /* 首页快捷入口子页 key（空=停在首页网格） */
      quickTab: '',
      /* 当前列表 key（空=非列表视图） */
      curKey: '',
      profile: {},

      /* 权限 */
      authDenied: false,
      authMsg: '',

      /* 列表运行时（同一时刻只渲染一张列表，故共用一份状态） */
      rows: [],
      page: 1,
      listTotal: 0,
      loading: false,
      finished: false,
      loaded: false,
      listError: false,
      /* 【P296】下拉刷新单闸：防 reset() 触发的 van-list 自动 load
         与 onRefresh 的兜底补调并发成两次请求（数据重复 / 卡在刷新中）。*/
      _prBusy: false,
      /* 【P296】消息三 Tab 的角标（由 calcMsgCounts 从全量消息聚合） */
      msgCounts: { unread: 0, pending: 0, total: 0 },
      /* 【P415】WS 实时总未读（m1+m2+m3），用于底部「消息」Tab 的红色气泡 */
      msgUnreadAll: 0,
      /* 【P296】「全部」Tab 拉到的全量消息缓存，供子 Tab 切回时复算角标 */
      _msgFullCache: [],
      /* 【P301 全站消息统一】4 Tab 当前值 + 组件数据源 + 后端能力位 */
      msgTab: 'unread',
      msgTabRows: [],
      msgLoading: false,
      msgIsCoreRoleRaw: null,
      /* 【P304】上滑分页状态（MessagePanel paged=true 时驱动 van-list）
         msgListLoading 必须独立于 msgLoading：van-list 是 v-model:loading 双向状态机，
         与下拉刷新的 msgLoading 共用会互相打断导致卡死。 */
      msgPage: 1,
      msgPageSize: 20,
      msgListLoading: false,
      msgFinished: false,
      /* 【P296】列表强制重挂载计数：绑到 van-list 的 :key，
         解决「curKey 不变 ⇒ 组件不重挂 ⇒ reset() 不触发 watcher ⇒ 列表停在旧数据」 */
      _listKey: 0,
      refreshing: false,
      /* 【P112】首页（含快捷入口）下拉刷新 */
      homeRefreshing: false,
      /* 【P116】销售人员筛选（支付单/开票/退款）：滑动列表 + 全部销售人员 */
      ownerPickShow: false,
      salesPool: [],
      kw: '',
      /* 状态标签筛选（服务端） */
      statusKey: '',
      /* 排序（前端，口径与销售端一致） */
      sortKey: '',
      /* 高级筛选值（服务端） */
      advValues: {},
      /* 头部高度（fixed 头部占位） */
      headerH: 0,

      /* 面板 */
      sortSheetShow: false,
      advShow: false,
      /* 【P419】会议室订单走 RoomFilter（公共组件），与本页「高级筛选」面板互斥 */
      roomFilterShow: false,
      advPickShow: false,
      advPickField: null,
      advPickTitle: '',
      advPickColumns: [],
      /* 【P175】日期区间筛选（van-calendar 区间选择） */
      calendarShow: false,
      advDateField: null,
      advDateDraft: [],

      /* 【P187】通用选择器（关联单号 / 门店 / 会议室 三类共用） */
      pickerShow: false,
      pickerField: null,
      pickerTitle: '',
      pickerSearch: '',
      pickerList: [],
      pickerLoading: false,

      /* 【P187】「是否显示合计金额」——按列表 key 分别记忆，默认关闭
         （承接 P179「合计金额先隐藏」的口径；开关打开后取服务端「当前筛选条件下全量合计」） */
      sumOn: {},
      sumAmount: null,
      sumLoading: false,

      /* 详情 */
      detailShow: false,
      detailLoading: false,
      detailTitle: '单据详情',
      detailGroups: [],
      detailRow: null,
      /* 【P207】详情文档（docDetail 返回的 document 全字段，含解析好的 create_user_name） */
      detailDoc: {},
      detailCanApprove: false,
      /* 【P207】支持「批量审核」的列表 key（支付单/发票/退款单；后端三者均下发 can_approve） */
      BATCH_KEYS: ['payments', 'invoices', 'refunds'],
      /* 【P207】详情备注（展示 / 添加 / 编辑） */
      detailRemark: '',
      detailRemarkEdit: null,
      detailRemarkDraft: '',
      remarkSaving: false,
      /* 【P205 2.0001】详情页审批状态文案（支付单详情展示「待我审核 / 已通过」等） */
      detailApprovalText: '',

      /* 【P205 2.0001】列表批量审核（支付单） */
      batchMode: false,
      batchChecked: [],

      /* 【P100】审批驳回：原因输入弹层 */
      rejectShow: false,
      rejectRow: null,
      rejectLabel: '',
      rejectRemark: '',
      /* 【P205 2.0001】批量驳回待处理行（非空时 onRejectBeforeClose 走批量分支） */
      batchRejectRows: [],

      /* 统计 */
      stat: { range: {} },
      statLoading: false,

      /* 底栏：「待办」已迁至首页快捷入口，底栏只保留 4 项 */
      bottomTabs: [
        { key: '',         label: '首页',     icon: 'wap-home-o' },
        /* 【P116】需求：回款支付 → 支付单（P191 复核确认已生效）；发票 → 开票 */
        { key: 'payments', label: '支付单',   icon: 'gold-coin-o' },
        { key: 'invoices', label: '开票',     icon: 'records' },
        { key: 'refunds',  label: '退款',     icon: 'cash-back-record' }
      ],
      /* 快捷入口：「待办」置首（带未处理气泡），其余按需求固定顺序 */
      quickItems: [
        /* 【P113】需求 02：「待办」改为「消息」—— 消息列表内不可操作，点卡片进详情审核 */
        { key: 'msg',           label: '消息',       badgeKey: 'msg', icon: svg('<path d="M4 19V5l8 4 8-4v14"/><path d="M4 9l8 4 8-4"/>') },
        { key: 'sellContracts', label: '销售合同',   icon: svg('<path d="M7 3h7l4 4v14H7z"/><path d="M14 3v4h4"/><path d="M9.5 12.5h5M9.5 16h3.5"/>') },
        { key: 'finContracts',  label: '财务合同',   icon: svg('<path d="M12 3v18"/><path d="M16 6.5h-5.2a2.8 2.8 0 1 0 0 5.6h2.4a2.8 2.8 0 1 1 0 5.6H8"/>') },
        { key: 'room',          label: '会议室订单', icon: svg('<path d="M4 20V10l8-6 8 6v10H4z"/><path d="M10 20v-5h4v5"/>') },
        { key: 'valueAdded',    label: '增值订单',   icon: svg('<rect x="4" y="10" width="16" height="10" rx="1.5"/><path d="M4 10 6 4h12l2 6"/><path d="M12 4v6M9 13.5h6"/>') },
        /* 【P113】需求 04：宫格「采购单」先隐藏（保留配置，去掉 hidden 即恢复） */
        { key: 'purchase',      label: '采购单',     hidden: true, icon: svg('<circle cx="9.5" cy="19.5" r="1.4"/><circle cx="17" cy="19.5" r="1.4"/><path d="M3 4h2.5l2.4 10.5h10.4L21 7H6"/>') },
        { key: 'stat',          label: '统计',       icon: svg('<path d="M5 19V9"/><path d="M12 19V5"/><path d="M19 19v-7"/>') }
      ]
    }
  },
  computed: {
    /* 【P205 2.0001】当前页「待我审核」的条数（批量入口显隐判据）
       【P207】放开到 支付单 / 发票 / 退款单 三类（后端三者均已下发 can_approve） */
    approvableCount() {
      if (this.BATCH_KEYS.indexOf(this.curKey) < 0) { return 0 }
      return this.rows.filter(r => this.rowCanApprove(r)).length
    },
    /* 当前列表是否支持批量审核（【P207】支付单 / 发票 / 退款单） */
    batchSupported() {
      return this.BATCH_KEYS.indexOf(this.curKey) >= 0
    },
    /* 是否处于「首页 → 快捷入口子页」：显示返回箭头 */
    isSub() {
      return this.tabIndex === 0 && !!this.quickTab
    },
    /* 【P92】当前快捷入口子页是否可交给统一引擎（BillList）渲染
       【P187-fix】收敛为仅 `plans`（回款计划）：其余 7 类全部走财务端自研列表，
       以取得 P187 的「高级筛选 + 门店↔会议室二级联动 + 服务端全量合计开关」能力。 */
    finBillType() {
      if (!this.quickTab) { return '' }
      return BILL_ENGINE_TYPES[this.curKey] || ''
    },
    /* 【P113】需求 04：宫格按 hidden 过滤（「采购单」先隐藏；去掉 hidden 即恢复） */
    quickTiles() {
      return (this.quickItems || []).filter(q => !q.hidden)
    },
    curSpec() {
      return LISTS[this.curKey] || null
    },
    curTitle() {
      if (this.curKey === 'stat') { return '统计报表' }
      return this.curSpec ? this.curSpec.title : ''
    },
    curIcon() {
      return this.curSpec ? this.curSpec.icon : 'records'
    },
    statusTabs() {
      return STATUS_TABS[this.curKey] || [ALL]
    },
    advFields() {
      return ADV_FIELDS[this.curKey] || []
    },
    /* ============================================================
     * 【P419】会议室订单：列表卡片与筛选面板改用全站公共组件
     * ------------------------------------------------------------
     * 为什么只对 room 生效：其余 10 类列表仍用本页配置驱动的自研卡片
     *   （LISTS + ADAPTERS + ADV_FIELDS），换掉会丢 P187 的排序 / 全量合计 / 批量审核，
     *   而会议室订单的公共卡片能力已与销售端 1:1，切换零损失。
     * ============================================================ */
    isRoomList() {
      return this.curKey === 'room'
    },
    /** RoomFilter 的「已生效值」：把本页 advValues / statusKey 反译成组件键名，
     *  保证再次打开面板时能正确回显（组件只知道 sn 与自己的键名）。 */
    roomFilterApplied() {
      const v = this.advValues || {}
      const o = {}
      if (v.on_site_from) { o.on_site_time_begin = v.on_site_from }
      if (v.on_site_to) { o.on_site_time_end = v.on_site_to }
      if (v.time_type !== undefined && v.time_type !== '') { o.time_type = v.time_type }
      /* 财务端顶部标签筛的就是「收费/免费」（statusParam=is_free）⇒ 归到组件的是否收费维度 */
      if (this.statusKey !== '' && this.statusKey != null) { o.is_free = this.statusKey }
      if (v.status !== undefined && v.status !== '' && RT_STATUS_TO_APPROVAL[v.status]) {
        o.approval_tab = RT_STATUS_TO_APPROVAL[v.status]
      }
      if (v.meeting_room_sn) { o.room_sn = v.meeting_room_sn }
      if (v.stores_sn) { o.stores_sn = v.stores_sn }
      ;['customer_sn', 'opportunitie_sn', 'parent_sn', 'contracts_sn'].forEach(k => {
        if (v[k]) { o[k] = v[k] }
      })
      return o
    },
    /** RoomFilter 的「已选中文名」：门店/会议室/4 个关联单据存的是 sn，界面要显示名字 */
    roomFilterMeta() {
      const v = this.advValues || {}
      return {
        room_sn: v.meeting_room_sn_text || '',
        stores_sn: v.stores_sn_text || '',
        customer_sn: v.customer_sn_text || '',
        opportunitie_sn: v.opportunitie_sn_text || '',
        parent_sn: v.parent_sn_text || '',
        contracts_sn: v.contracts_sn_text || ''
      }
    },
    emptyHint() {
      if (this.curKey === 'msg' || this.curKey === 'todo') { return '当前没有待你处理的消息' }
      if (this.curKey === 'purchase') { return '采购单数据尚未产生' }
      return '可调整筛选条件后重试'
    },
    avatarText() {
      const n = this.profile.staff_name || this.profile.name || ''
      return n ? n.slice(0, 1) : '财'
    },
    deptText() {
      const d = this.profile.dept_names
      return (d && d.length) ? d.join(' · ') : ''
    },
    roleText() {
      const r = this.profile.role_names
      return (r && r.length) ? r.join(' · ') : ''
    },
    todoCount() {
      const n = this.profile.pending_todo
      return (n != null && n !== '') ? Number(n) : 0
    },
    /* 【P116】当前选中的销售人员 id（空串 = 全部销售人员） */
    ownerId() {
      const v = this.advValues ? this.advValues.owner_id : ''
      return (v === null || v === undefined) ? '' : String(v)
    },
    ownerLabel() {
      if (!this.ownerId) { return '全部销售人员' }
      const hit = (this.salesPool || []).find(x => String(x.id) === this.ownerId)
      return hit ? hit.name : '全部销售人员'
    },
    /* 【P179】选择器标题随底层字段变化：
       销售合同 / 财务合同 的表按 owner_id 归属 ⇒ 文案「归属销售」；
       其余（支付/开票/退款/会议室/增值）后端按 create_user 过滤 ⇒ 文案「创建销售」。
       ps：两者语义不同，统一写「销售人员」会让用户误以为筛的是同一个字段，故区分。 */
    ownerFieldLabel() {
      /* 【P207】消息列表的该维度语义 = 审批提交人 */
      if (this.curKey === 'msg') { return '提交人' }
      if (this.curKey === 'sellContracts' || this.curKey === 'finContracts') { return '归属销售' }
      return '创建销售'
    },
    /* van-picker 列：第一项固定「全部销售人员」
       ⚠️ 必须是【扁平数组】（与 advPickColumns 同口径）；传嵌套 [[...]] 滚轮会是空的 */
    ownerColumns() {
      const list = this.salesPool || []
      return [{ text: '全部销售人员', value: '' }].concat(
        list.map(x => ({ text: x.name + (x.phone ? '（' + x.phone + '）' : ''), value: x.id }))
      )
    },
    /* 【P116】详情补「创建人」：优先单据创建人，兜底负责人
       【P207】★ 列表行的 create_user_name 对部分单据（支付单/退款单/发票）为空，
       而后端 docDetail 的 document 里是解析好的 ⇒ 必须先取详情文档，避免 hero 显示「创建人：-」。 */
    detailCreateUser() {
      const d = this.detailDoc || {}
      const r = this.detailRow || {}
      return d.create_user_name || r.create_user_name || r.owner_name || ''
    },

    /* 【P203】筛选按钮的激活态：只看「顶部状态标签 + 高级筛选」，
       不含排序 —— 排序已有独立按钮与独立激活态（见 sortActive）。
       旧口径（P175）把 sortKey 也算进来，导致「只排序不筛选」时
       筛选按钮也变蓝，误导用户以为筛过。 */
    filterActive() {
      const adv = Object.keys(this.advValues).some(k => this.advValues[k] !== '' && this.advValues[k] != null)
      return this.statusKey !== '' || adv
    },
    /* 【P203】排序按钮的激活态 */
    sortActive() {
      return !!this.sortKey
    },
    /* 【P203】「已筛选」状态条是否显示：筛选与排序任一有效都显示
       （状态条文案 filterSummary 里同时含排序名，故不能复用 filterActive） */
    filterBarVisible() {
      return this.filterActive || !!this.sortKey
    },
    filterSummary() {
      const parts = []
      if (this.statusKey !== '') {
        const t = this.statusTabs.find(x => x.value === this.statusKey)
        if (t) { parts.push(t.label) }
      }
      this.advFields.forEach(f => {
        /* 【P175/P187】已填维度统一用 advText 生成文案（date/ref/store/room 三类格式不同） */
        if (this.advFilled(f)) {
          parts.push(f.label + '：' + this.advText(f))
        }
      })
      const sortName = SORT_FULL_TEXT[this.sortKey]
      if (sortName) { parts.push(sortName) }
      return parts.length ? ('已筛选：' + parts.join(' / ')) : ''
    },
    /* 【P175】van-calendar 的可用区间：往前 8 年、往后 2 年（覆盖库内 2020~2027 数据） */
    calMinDate() {
      const d = new Date()
      d.setFullYear(d.getFullYear() - 8, 0, 1)
      return d
    },
    calMaxDate() {
      const d = new Date()
      d.setFullYear(d.getFullYear() + 2, 11, 31)
      return d
    },
    /* 【P187】合计开关是否开启（按列表 key 记忆）
       仅本 5 类有服务端全量合计能力（与后端 _listSpecMap 的 key 一一对应） */
    sumVisible() {
      return !!this.sumOn[this.curKey]
    },
    /* 【P187】是否支持「全量合计」（后端 listSum 支持的 5 类） */
    sumSupported() {
      return !!SUM_LIST_KEYS[this.curKey]
    },
    /* 【P187】合计金额文案
       ★ 口径 = 服务端「当前筛选条件下的全量」（listSum 返回），
         不是「已加载页累加」—— 后者翻页后会变大，会误导。
       未拿到数据时显示「—」，避免出现「¥0.00」这种看似确定的错误数字。 */
    sumText() {
      if (this.sumLoading) { return '统计中…' }
      if (this.sumAmount === null || this.sumAmount === undefined) { return '—' }
      return money(this.sumAmount)
    },
    /* 【P187】会议室选择器在未选门店时是否提示先选门店
       （会议室表 stores 列存门店 sn，未选门店时会拉全量 191 条，可用但不够精准） */
    roomPickHint() {
      const hasStore = !!(this.advValues && this.advValues.stores_sn)
      return hasStore ? '' : '未选门店时为全部会议室'
    },
    /* 【P202】排序动作（独立入口，已不含「高级筛选…」——
       高级筛选由头部「筛选」按钮一步直达，不再经此中转） */
    sortActions() {
      return [
        { name: '默认排序（最新在前）', value: '', kind: 'sort' },
        { name: '金额从高到低', value: 'amount-desc', kind: 'sort' },
        { name: '金额从低到高', value: 'amount-asc', kind: 'sort' },
        { name: '日期从新到旧', value: 'date-desc', kind: 'sort' },
        { name: '日期从旧到新', value: 'date-asc', kind: 'sort' }
      ].map(o => Object.assign({}, o, {
        className: this.sortKey === o.value ? 'van-action-sheet__item--active' : ''
      }))
    },
    /* 【P202】高筛已填维度数（筛选按钮角标）。
       判据与 filterActive 的 adv 部分一致：advValues 中非空键的个数 */
    advCount() {
      return Object.keys(this.advValues).filter(k => this.advValues[k] !== '' && this.advValues[k] != null).length
    },
    /* 【P202】排序按钮文案：未排序 =「排序」；已排序 =「金额 ↓」/「日期 ↑」 */
    sortBtnText() {
      return SORT_BTN_TEXT[this.sortKey] || '排序'
    },
    /* 【P203】排序按钮图标：未排序 = 通用排序；已排序 = 升/降序箭头（视觉强化当前方向） */
    sortBtnIcon() {
      if (this.sortKey === 'amount-asc' || this.sortKey === 'date-asc') { return 'ascending' }
      if (this.sortKey === 'amount-desc' || this.sortKey === 'date-desc') { return 'descending' }
      return 'sort'
    },
    /* 【P202】排序面板顶部「当前：…」说明行文案 */
    sortCurrentText() {
      return SORT_FULL_TEXT[this.sortKey] || '默认排序（最新在前）'
    },
    statCards() {
      const s0 = this.stat || {}
      const g = (o, k) => (o && o[k] != null ? o[k] : 0)
      const m = v => '¥' + Number(v || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      return [
        { k: 'p',  label: '已确认回款', value: m(g(s0.payment, 'paid_total')), extra: '区间内 ' + m(g(s0.payment, 'paid_in_range')) },
        { k: 'r',  label: '退款总额',   value: m(g(s0.refund, 'total')),       extra: '区间内 ' + m(g(s0.refund, 'in_range')) },
        { k: 'i',  label: '开票金额',   value: m(g(s0.invoice, 'total')),      extra: '共 ' + g(s0.invoice, 'count') + ' 张' },
        { k: 'fc', label: '财务合同',   value: g(s0.finance_contract, 'count'), extra: '金额 ' + m(g(s0.finance_contract, 'amount')) },
        { k: 'ar', label: '合同应收',   value: m(g(s0.finance_contract, 'receivable')), extra: '财务合同口径' },
        { k: 'ap', label: '待我审批',   value: g(s0.approval, 'pending_mine'),  extra: '全公司待办 ' + g(s0.approval, 'pending_all') }
      ]
    },
    /* 【P301 全站消息统一】4 Tab 固定：未读 → 待处理 → 待阅驳回 → 全部
       * value 与财务端后端 msg_status 的映射：2/0/3/空
       * ★ 必须在 computed 里 —— 放 methods 会被当普通方法，
       *   模板按 getter 求值拿不到值 ⇒ Tab 一个都不渲染（实测 tabs=[]）。 */
    msgTabsView() {
      const c = this.msgCounts || {};
      return [
        { value: 'unread',  label: '未读',     count: Number(c.unread)  || 0 },
        { value: 'pending', label: '待处理',   count: Number(c.pending) || 0 },
        { value: 'reject',  label: '待阅驳回', count: Number(c.reject)  || 0 },
        { value: 'all',     label: '全部',     count: 0 },
      ];
    },
    /* 【P301】后端下发的「四大岗位」能力位（财务人员属于四岗位，但以后端为准） */
    msgIsCoreRole() {
      return this.msgIsCoreRoleRaw !== null ? !!this.msgIsCoreRoleRaw : true;
    },
    /* 【P301】Tab → 财务端后端 msg_status 映射
       * ★★ **不能带参数**：放在 computed 里就是 getter，Vue 不会传参，
       *   带参数会永远拿不到 tab 而恒返回 ''（= 全部），
       *   表现为「切 Tab 后列表内容不变、active 错乱」。
       *   正确写法：由本 computed 内部读 this.msgTab。 */
    msgStatusForTab() {
      return { unread: '2', pending: '0', reject: '3', all: '' }[this.msgTab] || '';
    }

  },
  created() {
    this.profile = getProfile() || {}
    /* 【P187】读回「是否显示合计金额」的按列表记忆状态（默认关闭） */
    this.initSumSwitch()
    this.loadProfile()
  },
  mounted() {
    this.measureHeader()
    window.addEventListener('resize', this.measureHeader)
    /* 【P415】接入全局 WS：消息气泡实时刷新
       财务端此前**完全没有**引用 crmWs，未读角标只在「进消息页 / 处理消息」时
       靠 loadMsgCounts() 拉一次 ⇒ 新消息到达时气泡纹丝不动，必须手动切页或刷新，
       这正是「财务端消息气泡不更新」的根因之一。
       口径：服务端 MessageBadgeService 推送 {m1,m2,m3,total}，
             m1=通知未读 / m2=待处理 / m3=待阅驳回。 */
    try {
      crmWs.connect()
      this._offUnread = crmWs.on('unread', (d) => this.onUnread(d))
    } catch (e) { console.error('[finance] ws connect failed', e) }
  },
  updated() {
    this.measureHeader()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.measureHeader)
    /* 【P415】退订 WS，避免组件销毁后仍回调（内存泄漏 / 已销毁实例告警） */
    if (this._offUnread) { try { this._offUnread() } catch (e) {} this._offUnread = null }
  },
  methods: {
    /* ============================================================
       【P415】WS 推送 → 消息气泡实时刷新
       ------------------------------------------------------------
       问题现象：市场/销售/财务/运营四端的「消息气泡」不更新。
       后端根因：MessageBadgeService::countBy() 用了 `id IN (字符串子查询)`，
                 TP 实际编译成 `id = ( SELECT ... )` 且与 owner_id 之间是 AND，
                 导致计数恒为 0（已修）。
       前端根因：财务端根本没接 WS；运营端只 reconnect/close 没订阅 unread。
       本方法即为财务端补上的订阅消费端。
       ------------------------------------------------------------
       payload 口径（与后端 MessageBadgeService::refresh 一致）：
         m1 通知未读(type=0,is_read=0) / m2 待处理(type=1,is_action=0)
         m3 待阅驳回(type=2,is_read=0) / total = m1+m2+m3
       ============================================================ */
    onUnread(d) {
      if (!d || typeof d !== 'object') { return }
      const m1 = Number(d.m1) || 0
      const m2 = Number(d.m2) || 0
      const m3 = Number(d.m3) || 0
      const total = (typeof d.total !== 'undefined') ? (Number(d.total) || 0) : (m1 + m2 + m3)

      /* 【P417】★ 不再用徽标值覆盖三个 Tab 计数。
         徽标（m1/m2/m3）是 owner_id ∪ receiver 并集口径，而 Tab 计数
         （msgCounts）来自 messageSummary 的 receiver 口径，两者数值不等
         ⇒ 每 5s 被徽标覆盖一次、又被接口回写一次 ⇒ Tab 上的数字与气泡一起「闪」。
         现口径：msgCounts 只由 loadMsgCounts()/loadMsgSummary() 的 REST 结果写；
         徽标只用来驱动底部「消息」Tab 的红色气泡，并且仅当变化时才触发列表/计数刷新。 */
      /* ② 底部「消息」Tab 的红色气泡 */
      this.msgUnreadAll = total

      if (this._lastWsUnread === total) { return }
      this._lastWsUnread = total

      /* ③ 正在消息页：把列表也同步刷一遍，避免「气泡变了、列表还是旧的」 */
      if (this.curKey === 'msg') {
        try { this.loadMsgList() } catch (e) {}
        try { this.loadMsgCounts() } catch (e) {}
      }
    },
    /* 【P415】底部 Tab 气泡：仅「消息」显示（未读 + 待处理 + 驳回） */
    tabBadgeOf(key) {
      if (key !== 'msg') { return '' }
      const n = Number(this.msgUnreadAll) || 0
      if (n <= 0) { return '' }
      return n > 99 ? '99+' : String(n)
    },
    /* 【P244】消息 Tab 红色气泡角标：未读 / 待处理（全部不显示） */
    /* 【P296】★★ 消息角标必须用**消息**数据，不能用 this.rows。
       this.rows 是当前 Tab（未读/待处理/全部）拉到的分页结果：
         · 停在「未读」Tab 时 rows 里没有已处理消息 ⇒ 「待处理」角标恒 0；
         · 停在「全部」第 1 页时又只统计了前 10 条 ⇒ 角标偏小。
       P272 在独立页架构下用 msgCounts（全量一次拉完再算），这里保持同样口径：
       进消息页 / 切 Tab 时额外拉一次全量（msg_status=''），用它算两个角标。
       与后端口径严格一致（P277）：未读 = type=0 且 is_read=0；待处理 = type=1 且 is_action=0。 */
    msgTabCount(v){
      const c = this.msgCounts || {};
      if (v === '2') { return Number(c.unread) || 0; }
      if (v === '0') { return Number(c.pending) || 0; }
      return 0;
    },
    /* 【P296】按后端口径从全量消息聚合两个角标（与列表严格同源，不受分页影响） */
    calcMsgCounts(fullRows) {
      const arr = Array.isArray(fullRows) ? fullRows : [];
      this.msgCounts = {
        unread:  arr.filter(x => Number(x.type) === 0 && Number(x.is_read) === 0).length,
        pending: arr.filter(x => Number(x.type) === 1 && Number(x.is_action) === 0).length,
        /* 【P301】待阅驳回 = type=2 且未读 */
        reject:  arr.filter(x => Number(x.type) === 2 && Number(x.is_read) === 0).length,
        total:   arr.length,
      };
    },
    /* 【P296】★ 独立拉一次「全量」消息，只为算三 Tab 角标（不进列表）。
       为什么必须独立请求：列表是分页的（pageSize=10），onLoad 拿到的 `list`
       随翻页变化；若拿它算角标，切到第 2 页后角标就变成「最后一页的口径」
       （实测 unread 直接变 0）。这里固定 pageSize=200 一次拉完，口径稳定。 */
    loadMsgCounts() {
      if (this.curKey !== 'msg') { return; }
      financeApi.financeMsgList({ page: 1, pageSize: 200, msg_status: '' }).then(res => {
        /* 【P301】res 为空 = 登录失效（request 拦截器已改 reject）；
           否则给出 code 诊断信息。两种情况都要**清空列表**，
           否则旧的行会留在界面上、让人以为还有数据。 */
        if (!res) {
          this.msgErr = '登录已失效，请重新登录';
          this.msgTabRows = [];
          return;
        }
        if (res.code !== 200) {
          this.msgErr = '接口异常 code=' + res.code + ' ' + (res.msg || '');
          this.msgTabRows = [];
          return;
        }
        const d = res.data || {};
        /* 【P301】顺带接收后端下发的「四大岗位」能力位 */
        if (typeof d.is_core_role !== 'undefined') {
          this.msgIsCoreRoleRaw = !!d.is_core_role;
        }
        const list = Array.isArray(d.data) ? d.data : [];
        this._msgFullCache = list;
        this.calcMsgCounts(list);
      }).catch(() => { /* 角标失败不打断列表 */ });
    },
    /* ---------------- 【P301 全站消息统一】财务端消息（走 MessagePanel） ---------------- */
    /** 切 Tab：重置数据并按新 Tab 重新取数
     *  ★ 入参兼容两种形态：MessagePanel 的 `@tab` 抛的是**字符串**（`onSwitchTab(t.value)`），
     *    而其他端的 onMsgTab 按对象 `{value,label}` 取 —— 直接写 t.value 会得到 undefined，
     *    把 msgTab 置成 undefined ⇒ 组件 batchOn/取数全部失效（实测「全部」批量条不出现）。 */
    onMsgTab(t) {
      const val = (t && typeof t === 'object') ? (t.value || t) : t;
      if (!val || this.msgTab === val) { return; }
      this.msgTab = val;
      this.msgTabRows = [];
      this.loadMsgList();
    },
    /** 按当前 Tab 取消息列表（映射财务端后端 msg_status）
     *  【P304】★★ 修「向下滚动分页失效 / 不能滑动」
     *   原实现两处致命问题：
     *     ① `page: 1, pageSize: 50` **硬编码第一页**，滚多少次都只拿第 1 页；
     *     ② 全站没有任何一处以 reset=false 调用本方法 ⇒ 上拉加载事件从未接线。
     *   现在：reset=true（刷新/切 Tab）→ 归第 1 页并清 finished；
     *        reset=false（上滑触底）→ 沿用 msgPage 继续往后翻。 */
    loadMsgList(reset) {
      const isReset = reset === undefined ? true : !!reset;
      if (isReset) { this.msgPage = 1; this.msgFinished = false; }
      else if (this.msgListLoading || this.msgFinished) { return; }
      /* 两条路径都占 listLoading 闸门：首屏请求在途时 van-list 会 check()，
         看到 loading=false 就并发再发一次 page=1 ⇒ 列表出现重复卡片。 */
      this.msgListLoading = true;
      this.msgLoading = true;
      financeApi.financeMsgList({
        page: this.msgPage,
        pageSize: this.msgPageSize,
        /* ★ msgStatusForTab 定义在 computed（无参取值），不是方法 ⇒ 不能带 () 调用 */
        msg_status: this.msgStatusForTab,
      }).then(res => {
        this.msgLoading = false;
        this.msgListLoading = false;
        if (!res || res.code !== 200) {
          this.msgErr = '接口异常 code=' + (res ? res.code : '-');
          if (isReset) { this.msgTabRows = []; }
          /* 【P304】失败即到底，避免 van-list 停在本页反复重试；下拉刷新会重新打开闸门 */
          this.msgFinished = true;
          return;
        }
        const d = res.data || {};
        /* 【P301】接收后端下发的「四大岗位」能力位 */
        if (typeof d.is_core_role !== 'undefined') {
          this.msgIsCoreRoleRaw = !!d.is_core_role;
        }
        this.msgErr = '';
        const rows = Array.isArray(d.data) ? d.data : [];
        this.msgTabRows = isReset ? rows : this.msgTabRows.concat(rows);
        /* 【P304】到底判定：后端 total 优先，其次「本页不足一页」 */
        const total = (d.total != null) ? Number(d.total) : null;
        this.msgFinished = (rows.length < this.msgPageSize)
          || (total != null && this.msgTabRows.length >= total);
        if (!this.msgFinished) { this.msgPage = this.msgPage + 1; }
        console.log('[MSG_FLOW] FIN loadMsgList', {
          tab: this.msgTab, reset: isReset, page: this.msgPage,
          rows: rows.length, acc: this.msgTabRows.length, total: total, finished: this.msgFinished
        });
        /* 拉到全量时顺带刷新角标 */
        if (this.msgTab === 'all' && isReset) {
          this._msgFullCache = this.msgTabRows.slice();
          this.calcMsgCounts(this._msgFullCache);
        }
      }).catch(() => {
        this.msgLoading = false;
        this.msgListLoading = false;
        this.msgErr = '请求异常，请稍后重试';
        if (isReset) { this.msgTabRows = []; }
        this.msgFinished = true;
      });
    },

    /* 【P304】上滑触底 → 加载下一页 */
    onMsgLoadMore() {
      if (this.msgFinished) { return; }
      this.loadMsgList(false);
    },
    /** 点消息：进入详情（财务端原有 openDetail 逻辑） */
    openMsgDetail(row) {
      if (!row) { return; }
      this.openDetail(row);
    },
    /** 【P301】批量动作：未读=批量已读（msg_status='2'）；全部=批量删除 */
    onMsgBatch(payload) {
      const action = (payload && payload.action) || (this.msgTab === 'unread' ? 'read' : 'delete');
      const sns = (payload && payload.sns) ? payload.sns : [];
      if (!Array.isArray(sns) || !sns.length) {
        this.$toast('请先勾选'); return;
      }
      const api = action === 'read' ? financeApi.financeMsgReadBatch : financeApi.financeMsgDeleteBatch;
      if (typeof api !== 'function') {
        this.$toast('该操作暂不支持'); return;
      }
      this.msgBatchBusy = true;
      api({ sns }).then(res => {
        this.msgBatchBusy = false;
        if (res && res.code === 200) {
          this.$toast(res.msg || '操作成功');
          this.loadMsgList();
          this.loadMsgCounts();
        } else {
          this.$toast((res && res.msg) || '操作失败');
        }
      }).catch(() => {
        this.msgBatchBusy = false;
        this.$toast('操作失败');
      });
    },

    /* ---------------- 权限 / 画像 ---------------- */
    /* 直接打 finance/myProfile：命中即财务端；403 时给出明确可操作的权限提示，
       而不是静默空列表或让全局红条一闪而过。 */
    loadProfile() {
      suppressGlobalError()
      financeApi.financeMyProfile({}).then(res => {
        suppressGlobalError()
        if (res && res.code === 200) {
          this.authDenied = false
          this.profile = Object.assign({}, this.profile, res.data || {})
          /* 同步缓存端标识，避免下次进端又先探 market */
          import('@/util/clientAuth.js').then(m => {
            m.fetchProfile(true, 'finance').then(p => {
              if (p) { this.profile = Object.assign({}, this.profile, p) }
            })
          })
        } else {
          this.authDenied = true
          this.authMsg = (res && res.msg) || '当前账号无财务端访问权限'
        }
      }).catch(() => {
        suppressGlobalError()
        this.authDenied = true
        this.authMsg = '无法连接服务器，请检查网络后重试'
      })
    },
    refreshTodo() {
      /* 【P112】返回 Promise，供首页下拉刷新收尾 */
      return financeApi.financeTodoStat({}).then(res => {
        if (res && res.code === 200 && res.data) {
          this.profile = Object.assign({}, this.profile, { pending_todo: res.data.pending })
        }
      }).catch(() => {})
    },
    /* 【P112】首页（含快捷入口）下拉刷新
       刷新对象 = 画像（loadProfile，只读安全）+ 「待我审批」数（refreshTodo）。
       ★ 与销售端/市场端宫格下拉口径一致：刷的是「摘要 + 角标」，不是列表数据。
       收尾以 refreshTodo 的 Promise 为准（它有明确的 then/catch），loadProfile 本身
       用 suppressGlobalError() 吞错并有 authDenied 兜底，故不阻塞动画。 */
    onHomeRefresh() {
      const done = () => { this.homeRefreshing = false }
      try {
        this.loadProfile()
        const p = this.refreshTodo()
        if (p && typeof p.then === 'function') { p.then(done, done) }
        else { setTimeout(done, 800) }
      } catch (e) { done() }
    },
    measureHeader() {
      this.$nextTick(() => {
        const el = this.$refs.hd
        const h = el ? el.offsetHeight : 0
        if (h !== this.headerH) { this.headerH = h }
      })
    },

    /* ---------------- 【P116】销售人员筛选 ---------------- */
    fetchSalesPool() {
      financeApi.financeSalesPool({}).then(res => {
        if (res && res.code === 200 && res.data) {
          this.salesPool = res.data.list || []
        }
      }).catch(() => {})
    },
    openOwnerPick() {
      if (!(this.salesPool || []).length) { this.fetchSalesPool() }
      this.ownerPickShow = true
    },
    onOwnerPick(val) {
      const v = (val && val.value !== undefined && val.value !== null) ? val.value : ''
      this.ownerPickShow = false
      this.$set(this.advValues, 'owner_id', v === '' ? '' : v)
      this.reload()
    },
    clearOwner() {
      this.$set(this.advValues, 'owner_id', '')
      this.reload()
    },

    /* ---------------- 导航 ---------------- */
    onTabbarChange(idx) {
      this.quickTab = ''
      this.kw = ''
      this.sortKey = ''
      this.advValues = {}
      this.sumAmount = null
      this.statusKey = ''
      if (idx === 0) {
        this.curKey = ''
      } else {
        this.curKey = this.bottomTabs[idx].key
      }
      this.reset()
    },
    switchSeg(key) {
      if (this.curKey === key) { return }
      this.curKey = key
      this.kw = ''
      this.statusKey = ''
      this.advValues = {}
      this.sumAmount = null
      this.reset()
    },
    quickGo(q) {
      this.quickTab = q.key
      this.kw = ''
      this.sortKey = ''
      this.advValues = {}
      this.sumAmount = null
      this.statusKey = ''
      if (q.key === 'stat') {
        this.curKey = 'stat'
        this.loadStat()
        return
      }
      this.curKey = q.key
      /* 【P296】进消息页时默认落「全部」Tab 并预拉一次全量：
         ① 角标（未读/待处理）只能从全量聚合，子 Tab 的结果不含另一类消息；
         ② 「全部」数据量最大，列表更可能超出视口，下拉才有可拉的空间。
         否则停在默认 Tab 时角标常年为 0，且内容不满一屏导致下拉无反应。 */
      if (q.key === 'msg') {
        /* 【P296】进消息页：落「全部」Tab + 强制重新加载一次全量。
           ① 角标（未读/待处理）只能从全量聚合，子 Tab 的结果不含另一类消息；
           ②「全部」数据量最大，列表更可能超出视口，下拉才有可拉的空间。
           ★ 为什么不能只靠 reset() + van-list 自动 load：
             van-list 的 :key="curKey" 在 curKey 不变时**不会重新挂载**，
             而 reset() 把 loading 由 false 置回 false（无变化）⇒ watcher 不触发
             ⇒ 实测「消息→待处理→消息」这类往返后列表停在旧数据、只有 3 条。
             这里用 `_listKey` 递增强制重挂载，加 `this.$nextTick(() => this.onLoad())`
             保证首次/往返都必然取一次数。 */
        this.statusKey = ''
        this._msgFullCache = []
        this.msgCounts = { unread: 0, pending: 0, reject: 0, total: 0 }
        this._listKey = (this._listKey || 0) + 1
        this.reset()
        /* 【P301】财务端消息已改为走 MessagePanel 组件（不再用通用列表的 onLoad），
           ⇒ 进入消息页必须调 `loadMsgList()` 拉组件数据，否则列表恒空（实测 rows=0）。 */
        this.msgTabRows = []
        this.loadMsgList()
        /* ★ 角标必须用**独立的一次全量请求**，不能靠列表的分页结果：
             列表分页 size=10，滑到第 2 页时 onLoad 里的 `list` 只有第 2 页那几条
             ⇒ 若拿它覆盖 _msgFullCache，角标就被算成「最后一页」的口径（实测 unread 变 0）。
           这里单独打一次 msg_status='' & pageSize=200，只用于算角标，不进列表。 */
        this.loadMsgCounts()
        return
      }
      this.reset()
    },
    backHome() {
      this.quickTab = ''
      this.curKey = ''
      this.kw = ''
      this.sortKey = ''
      this.advValues = {}
      this.sumAmount = null
      this.statusKey = ''
      this.reset()
    },

    /* ---------------- 搜索 / 筛选 ---------------- */
    searchNow() {
      this.reload()
    },
    onStatusTab(t) {
      if (this.statusKey === t.value) { return }
      this.statusKey = t.value
      /* 【P296】切 Tab 同样递增 _listKey 强制重挂载（否则 curKey 不变时列表不重取） */
      this._listKey = (this._listKey || 0) + 1
      /* 切 Tab 后刷新角标（消息已读状态会变） */
      if (this.curKey === 'msg') { Vue.nextTick(() => this.loadMsgCounts()); }
      this.reload()
    },
    /* 【P202】「筛选」→ 一步直达高级筛选面板（取消原「筛选 / 排序」中间抽屉）
       用户诉求：「这些列表点开之后，一级抽屉取消换成直接打开高级筛选」 */
    openFilter() {
      /* 【P419】会议室订单 → 公共筛选面板（RoomFilter）；其余列表沿用本页高级筛选 */
      if (this.isRoomList) { this.roomFilterShow = true; return }
      this.advShow = true
    },
    /* 【P419】RoomFilter 确定：把组件键名反译成财务端 room 列表接口的参数键
       ------------------------------------------------------------------
       财务端 /finance/roomOrdersList 的参数口径与 BillList 引擎不同：
         · 日期区间是 on_site_from / on_site_to（引擎是 on_site_time_begin/_end）
         · 会议室是 meeting_room_sn（引擎 room_sn 亦可，但财务端只认前者）
         · 审核状态是 status（引擎是 approval_tab）
         · 收费/免费走顶部标签的 statusParam=is_free ⇒ 回写 statusKey
       这些都是既有后端能力（P187 已实现），本轮只做前端键名映射，后端零改动。 */
    onRoomFilterApply(payload, labels, meta) {
      const p = payload || {}
      const m = meta || {}
      const set = (k, v) => { this.$set(this.advValues, k, v) }
      /* 1) 使用时间 */
      set('on_site_from', p.on_site_time_begin || '')
      set('on_site_to', p.on_site_time_end || '')
      /* 2) 时段 */
      set('time_type', (p.time_type === undefined || p.time_type === null) ? '' : p.time_type)
      set('time_type_text', p.time_type === undefined || p.time_type === null ? ''
        : (RT_TIME_LABEL[p.time_type] || ''))
      /* 3) 是否收费 → 顶部标签（财务端 room 的 statusParam） */
      this.statusKey = (p.is_free === undefined || p.is_free === null) ? '' : String(p.is_free)
      /* 4) 审核状态：审批单状态(2/3/5) → 本表 status 列(0/1/2) */
      const apprTab = (p.approval_tab === undefined || p.approval_tab === null) ? '' : String(p.approval_tab)
      const stVal = (apprTab !== '' && RT_APPROVAL_TO_STATUS[apprTab] !== undefined) ? RT_APPROVAL_TO_STATUS[apprTab] : ''
      set('status', stVal)
      set('status_text', stVal === '' ? '' : (RT_STATUS_LABEL[stVal] || ''))
      /* 5) 会议室 / 门店 */
      set('meeting_room_sn', p.room_sn || '')
      set('meeting_room_sn_text', m.room_sn || '')
      set('stores_sn', p.stores_sn || '')
      set('stores_sn_text', m.stores_sn || '')
      /* 6) 四个关联单据 */
      ;['customer_sn', 'opportunitie_sn', 'parent_sn', 'contracts_sn'].forEach(k => {
        set(k, p[k] || '')
        set(k + '_text', m[k] || '')
      })
      /* 与顶部标签一致：RoomFilter 里的审核状态与「高级筛选」的 status 是同一列，
         这里已写进 advValues.status，buildParams 会原样下发。 */
      this.roomFilterShow = false
      this.reload()
    },
    /* 【P202】「排序」→ 打开独立排序面板 */
    openSort() {
      this.sortSheetShow = true
    },
    onSortAction(item) {
      this.sortSheetShow = false
      this.sortKey = item.value || ''
      this.applyLocalSort()
    },
    openAdvPick(f) {
      /* 【P175】日期维度：走 van-calendar 区间选择（date_from / date_to 两个服务端参数）
         【P187】多组日期：param 为列前缀（on_site / create / payment / signing / start） */
      if (f.type === 'date') {
        this.advDateField = f
        const v = this.advDateRangeOf(f.param)
        this.advDateDraft = (v && v.length === 2) ? [v[0], v[1]] : []
        this.$nextTick(() => { this.calendarShow = true })
        return
      }
      /* 【P187】关联单号 / 门店 / 会议室：走通用可搜索选择器 */
      if (f.type === 'ref' || f.type === 'store' || f.type === 'room') {
        this.openPicker(f)
        return
      }
      this.advPickField = f
      this.advPickTitle = '选择' + f.label
      this.advPickColumns = f.options.map(o => ({ text: o.text, value: o.value }))
      this.advPickShow = true
    },
    onAdvPick(item) {
      if (this.advPickField && item) {
        this.$set(this.advValues, this.advPickField.param, item.value)
      }
      this.advPickShow = false
    },
    /* 日期区间确认：写入 <param>_from / <param>_to
       【P187】param 支持多组（回款计划等仍用 date_from/date_to） */
    onAdvDateConfirm(range) {
      const arr = Array.isArray(range) ? range : [range]
      const fmt = (d) => {
        if (!d) { return '' }
        const x = (d instanceof Date) ? d : new Date(d)
        const p = (n) => (n < 10 ? '0' + n : '' + n)
        return x.getFullYear() + '-' + p(x.getMonth() + 1) + '-' + p(x.getDate())
      }
      const param = (this.advDateField && this.advDateField.param) || 'date_from'
      const from = fmt(arr[0])
      const to = fmt(arr[1] || arr[0])
      this.advDateDraft = (from && to) ? [arr[0], arr[1] || arr[0]] : []
      this.$set(this.advValues, param + '_from', from)
      this.$set(this.advValues, param + '_to', to)
      this.calendarShow = false
    },
    /* 【P187】取某组日期区间（[from, to] 或 []） */
    advDateRangeOf(param) {
      const a = this.advValues[param + '_from']
      const b = this.advValues[param + '_to']
      if (!a && !b) { return [] }
      const p = (s) => {
        const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/)
        return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null
      }
      const d1 = p(a), d2 = p(b)
      if (d1 && d2) { return [d1, d2] }
      if (d1) { return [d1, d1] }
      return []
    },
    advText(f) {
      /* 【P175/P187】日期维度文案：2026-01-01 ~ 2026-01-31 */
      if (f.type === 'date') {
        const a = this.advValues[f.param + '_from']
        const b = this.advValues[f.param + '_to']
        if (!a && !b) { return '请选择' }
        return (a || '不限') + ' ~' + (b || '不限')
      }
      /* 【P187】关联单号 / 门店 / 会议室：显示已选条目名（选择器里存了 _text） */
      if (f.type === 'ref' || f.type === 'store' || f.type === 'room') {
        const v = this.advValues[f.param]
        if (v === '' || v == null) { return f.type === 'room' ? (this.roomPickHint || '请选择') : '请选择' }
        const hit = (this.advValues[f.param + '_text'] || '')
        return hit || String(v)
      }
      const v = this.advValues[f.param]
      if (v === '' || v == null) { return '请选择' }
      const o = f.options.find(x => x.value === v)
      return o ? o.text : String(v)
    },

    /* 【P187】某维度是否已填写（模板高亮判据）
       date → 看 <param>_from / <param>_to；其余 → 看 f.param */
    advFilled(f) {
      if (!f) { return false }
      if (f.type === 'date') {
        return !!(this.advValues[f.param + '_from'] || this.advValues[f.param + '_to'])
      }
      const v = this.advValues[f.param]
      return !(v === '' || v == null)
    },
    /* 【P187】面板内联「×」清除：不打开选择器即可清掉该维度 */
    advClearable(f) {
      return !!f && (f.type === 'date' || f.type === 'ref' || f.type === 'store' || f.type === 'room')
    },
    clearAdvField(f) {
      if (!f) { return }
      if (f.type === 'date') {
        this.$set(this.advValues, f.param + '_from', '')
        this.$set(this.advValues, f.param + '_to', '')
        return
      }
      this.$set(this.advValues, f.param, '')
      this.$set(this.advValues, f.param + '_text', '')
      /* ★ 清门店时同步清会议室（二级联动一致性） */
      if (f.type === 'store') {
        this.$set(this.advValues, 'meeting_room_sn', '')
        this.$set(this.advValues, 'meeting_room_sn_text', '')
      }
    },

    /* ---------------- 【P187】通用选择器（关联单号 / 门店 / 会议室） ---------------- */
    openPicker(f) {
      this.pickerField = f
      this.pickerSearch = ''
      this.pickerList = []
      this.pickerTitle = f.label
      this.pickerShow = true
      this.fetchPickerList()
    },
    fetchPickerList() {
      const f = this.pickerField
      if (!f) { return }
      this.pickerLoading = true
      let p = null
      if (f.type === 'store') {
        p = financeApi.financeStoresOptions({ searchText: this.pickerSearch })
      } else if (f.type === 'room') {
        /* ★ 二级联动：带上当前已选门店（若有） */
        p = financeApi.financeRoomsOptions({
          searchText: this.pickerSearch,
          stores_sn: (this.advValues && this.advValues.stores_sn) || ''
        })
      } else {
        p = financeApi.financeFilterRefOptions({ kind: f.kind, searchText: this.pickerSearch, pageSize: 50 })
      }
      p.then(res => {
        this.pickerLoading = false
        const d = (res && res.data) || {}
        this.pickerList = Array.isArray(d.list) ? d.list : []
      }).catch(() => {
        this.pickerLoading = false
        this.pickerList = []
      })
    },
    onPickerSearch() {
      this.fetchPickerList()
    },
    /* 选择某条：写 advValues[param] = sn，同时记 advValues[param+'_text'] = 名称
       ★ 选门店时：清空已选会议室（二级联动的一致性） */
    onPickerSelect(item) {
      const f = this.pickerField
      if (!f || !item) { this.pickerShow = false; return }
      this.$set(this.advValues, f.param, item.value)
      this.$set(this.advValues, f.param + '_text', item.text)
      if (f.type === 'store') {
        this.$set(this.advValues, 'meeting_room_sn', '')
        this.$set(this.advValues, 'meeting_room_sn_text', '')
      }
      this.pickerShow = false
    },
    clearPicker() {
      const f = this.pickerField
      if (!f) { return }
      this.$set(this.advValues, f.param, '')
      this.$set(this.advValues, f.param + '_text', '')
      if (f.type === 'store') {
        this.$set(this.advValues, 'meeting_room_sn', '')
        this.$set(this.advValues, 'meeting_room_sn_text', '')
      }
      this.pickerShow = false
    },

    /* ---------------- 【P187】「是否显示合计金额」开关 ---------------- */
    initSumSwitch() {
      /* 从 localStorage 读回「按列表 key」记忆的开关状态，默认关闭 */
      const out = {}
      Object.keys(SUM_LIST_KEYS).forEach(k => {
        let v = ''
        try { v = window.localStorage.getItem(SUM_LS_PREFIX + k) || '' } catch (e) { v = '' }
        out[k] = (v === '1')
      })
      this.sumOn = out
    },
    toggleSum() {
      const k = this.curKey
      if (!SUM_LIST_KEYS[k]) { return }
      const next = !this.sumOn[k]
      this.$set(this.sumOn, k, next)
      try { window.localStorage.setItem(SUM_LS_PREFIX + k, next ? '1' : '0') } catch (e) {}
      if (next) { this.loadSum() } else { this.sumAmount = null }
    },
    /* 拉取「当前筛选条件下的全量合计」
       ★ 参数与列表接口完全一致（同 buildParams，去掉分页），保证口径同源 */
    loadSum() {
      const k = this.curKey
      if (!SUM_LIST_KEYS[k]) { this.sumAmount = null; return }
      if (!this.sumVisible) { this.sumAmount = null; return }
      const params = this.buildParams()
      delete params.page
      delete params.pageSize
      params.list = k
      this.sumLoading = true
      financeApi.financeListSum(params).then(res => {
        this.sumLoading = false
        if (res && res.code === 200 && res.data) {
          this.sumAmount = Number(res.data.amount || 0)
        } else {
          this.sumAmount = null
        }
      }).catch(() => {
        this.sumLoading = false
        this.sumAmount = null
      })
    },
    resetAdv() {
      this.advValues = {}
      this.advDateDraft = []
    },
    applyAdv() {
      this.advShow = false
      this.reload()
    },
    clearFilter() {
      this.statusKey = ''
      this.sortKey = ''
      this.advValues = {}
      this.sumAmount = null
      this.reload()
    },
    /* ---------------- 列表 ---------------- */
    reset() {
      this.rows = []
      this.page = 1
      this.listTotal = 0
      this.loading = false
      this.finished = false
      this.loaded = false
      this.listError = false
    },
    reload() {
      this.reset()
      this.onLoad()
      /* 【P187】合计口径 = 当前筛选条件下的全量 ⇒ 筛选一变必须重算 */
      if (this.sumVisible) { this.loadSum() }
      this.measureHeader()
    },
    /* 【P214】原实现手动调 this.onLoad()，与 van-list 的 finished watcher 双触发，
       导致同页数据被追加两遍（下拉刷新后列表重复）。
       改法：只 reset()，由 van-list 自然驱动唯一一次 load。 */
    /* 【P296】★★ 修下拉刷新的 JS 报错：原来的 `$$nextTick` 兜底补 onLoad 是**竞态源**。
       链路：reset() 把 loading 置 false → van-list 的 v-model:loading watcher 立刻 check()
             → 自动触发一次 onLoad；与此同时 $$nextTick 里的兜底判断
             `if (!this.loaded && !this.loading)` 恰好也成立 → 同一次下拉发**两个**请求，
             两个响应都执行 `this.rows = this.rows.concat(mapped)`
             ⇒ 数据重复 + 观感上「一直在刷新中」。
       改法：用 `_prBusy` 单闸只放行一条刷新路径，闸门由 onLoad 的收尾统一释放。 */
    onRefresh() {
      /* ★★ 【P296 修 JS 报错】`TypeError: t.$$nextTick is not a function`
         根因：这里写成两层嵌套 `this.$nextTick(() => this.$$nextTick(...))`，
         内层箭头函数的 this 沿词法作用域取到外层 Vue 实例 —— 但当 van-list 的
         watcher 在**组件销毁/重渲染**后仍持有旧闭包再调用时，实例上的
         `$$nextTick` 已被卸载清理 ⇒ 抛 TypeError。
         稳妥写法：统一用静态 `Vue.nextTick`，不依赖实例上的私有属性。
         （import 见文件头：import Vue from 'vue'） */
      if (this._prBusy) { return; }
      this._prBusy = true;
      this.reset();
      /* v-model 必须归位，否则 Vant 一直停在「刷新中」不松手（head 位移也回不来）。 */
      Vue.nextTick(() => {
        this.refreshing = false;
        /* 等 van-list 内部 watcher 跑完一拍再看是否已加载：
           已加载（列表自己驱动了）就收闸，未加载才补这一次 —— 不再无条件并发两次。 */
        Vue.nextTick(() => {
          if (!this.loaded && !this.loading) { this.onLoad(); }
          /* 消息页下拉后同步刷新三 Tab 角标（已读/待处理状态会变） */
          if (this.curKey === 'msg') { this.loadMsgCounts(); }
        });
      });
    },
    buildParams() {
      const params = { page: this.page, pageSize: 10 }
      if (this.kw) { params.searchText = this.kw }
      const sp = this.curSpec
      if (sp && sp.statusParam && this.statusKey !== '') {
        /* 【P214】消息的待处理 tab 取值就是 '0'，不能被 `!== ''` 过滤掉（原逻辑为真值判断） */
        /* 【P244】消息三 Tab → msg_status：''全部 / '0'待处理 / '2'未读
         *   ⚠️ 后端若未支持 '2' 会退化为不过滤（不报错，可先上线）。 */
        if (this.curKey === 'msg') {
          const _v = this.statusKey;
          params[sp.statusParam] = _v;
        } else {
          params[sp.statusParam] = this.statusKey;
        }
      }
      const self = this
      Object.keys(this.advValues).forEach(k => {
        /* 【P187】`*_text` 是「已选条目名」的本地镜像（仅用于面板回显），
           绝不能发给后端 —— 后端没有这些参数，发了也是无效字段。 */
        if (/_text$/.test(k)) { return }
        const v = self.advValues[k]
        if (v !== '' && v != null) { params[k] = v }
      })
      /* 【P191】`status` 双入口归一。
         适用范围：**顶部标签行也筛 status** 的列表 —— 支付单/开票/退款
         （statusParam === 'status'）。这两处 param 同名，正常不会同时被设；
         若用户先点标签再开面板选，advValues 会覆盖 statusKey，导致
         「面板显示 A、标签高亮 B」的错位。此处以「非空者」为准并双向回写。
         ⚠️ 会议室订单 statusParam 是 is_free（顶部标签筛的是收费/免费），
            其面板里的「审核状态」是独立维度（同样落在 status 列），
            二者语义正交、**不能合并**，故显式排除 room。
         ⚠️ 增值订单 statusParam 为 ''（无顶部状态标签），面板 status 独立生效。 */
      const spParam = sp && sp.statusParam ? sp.statusParam : ''
      if (spParam === 'status') {
        const fromAdv = this.advValues ? this.advValues.status : ''
        const fromTab = this.statusKey
        const effective = (fromAdv !== '' && fromAdv != null) ? fromAdv : fromTab
        if (effective !== '' && effective != null) {
          params.status = effective
          if (String(fromAdv) !== String(effective)) { this.$set(this.advValues, 'status', effective) }
          if (String(this.statusKey) !== String(effective)) { this.statusKey = effective }
        }
      }
      return params
    },
    onLoad() {
      /* 【P214 紧急修正】原在此加了 `if (this.loading) return` 互斥锁 —— 造成全端列表永久 loading！
         原因：van-list 挂载时会**先置 loading=true 再触发 onLoad**（immediate-check），
         于是唯一一次真实加载被自己的锁挡掉，loading 永不复位 ⇒ 所有列表卡在转圈。
         重复加载的正解是在 onRefresh 里不手动调 onLoad（已改），不需要互斥锁。 */
      const sp = this.curSpec
      if (!sp) {
        this.loading = false
        this.finished = true
        this.loaded = true
        this._prBusy = false   /* 【P296】提前返回也要释放刷新单闸 */
        return
      }
      const api = financeApi[sp.api]
      if (typeof api !== 'function') {
        this.loading = false
        this.finished = true
        this.loaded = true
        this._prBusy = false   /* 【P296】同上 */
        return
      }
      this.loading = true
      api(this.buildParams()).then(res => {
        this.loading = false
        if (!res || res.code !== 200) {
          /* 【P214】原在此置 finished=false，会再次触发 List 的 finished watcher 造成循环加载。
             listError=true 已足够阻断 check()，这里不再改 finished。 */
          this.listError = true
          if (res && res.code === 403) {
            this.authDenied = true
            this.authMsg = res.msg || '当前账号无财务端访问权限'
          }
          return
        }
        const d = res.data || {}
        const list = Array.isArray(d.data) ? d.data : []
        const adapter = ADAPTERS[this.curKey] || (x => x)
        /* 【P419-b】会议室订单字段归一：财务端 /finance/roomOrdersList 的 meeting_room_name
           只到会议室名（"人广港泰"），**不带房号**；而 client/operation/market 三端是
           "人广港泰H2"（name 的四段标题里也是 "【人广港泰H2】"）。
           卡片是同一套公共组件，若不补，财务端卡片会显示「会议室：人广港泰」而标题是
           「【人广港泰H2】」，同一条订单在四端长得不一样（用户要求四端样式同步）。
           只从标题第二段补，且仅当该段以原名称为前缀时才补（不做任何猜测性改写）。 */
        const normalize = (r) => {
          const o = Object.assign({}, r)
          if (this.curKey === 'room' && o.meeting_room_name && o.name) {
            const segs = String(o.name).match(/【[^】]*】/g) || []
            const seg = segs[1] ? segs[1].slice(1, -1) : ''
            if (seg && seg !== o.meeting_room_name && seg.indexOf(o.meeting_room_name) === 0) {
              o.meeting_room_name = seg
            }
          }
          return Object.assign(o, adapter(o))
        }
        const mapped = list.map(normalize)
        this.rows = this.rows.concat(mapped)
        this.listTotal = d.total != null ? Number(d.total) : this.rows.length
        this.page += 1
        this.loaded = true
        this.finished = this.rows.length >= this.listTotal || list.length === 0
        this.applyLocalSort()
        /* 【P296】消息列表：**不再**用分页结果覆盖角标。
           角标统一由 loadMsgCounts() 的独立全量请求负责（见该方法注释），
           这里若再写一次，滑到第 2 页就会把角标冲成「最后一页口径」。 */
        if (this.curKey === 'msg' && !this._msgFullCache.length) {
          this.loadMsgCounts();
        }
      }).catch(() => {
        this.loading = false
        this.listError = true
      })
      /* 【P296】释放下拉刷新单闸（成功/失败/403 都要放，否则后续下拉全部被吞） */
      .then(() => { this._prBusy = false })
    },
    /* 排序（前端，口径与销售端 billSort 一致） */
    applyLocalSort() {
      const k = this.sortKey
      if (!k) { return }
      const arr = this.rows.slice()
      if (k === 'amount-desc') {
        arr.sort((a, b) => (b._amountNum == null ? -Infinity : b._amountNum) - (a._amountNum == null ? -Infinity : a._amountNum))
      } else if (k === 'amount-asc') {
        arr.sort((a, b) => (a._amountNum == null ? Infinity : a._amountNum) - (b._amountNum == null ? Infinity : b._amountNum))
      } else if (k === 'date-desc') {
        arr.sort((a, b) => String(b._date || '').localeCompare(String(a._date || '')))
      } else if (k === 'date-asc') {
        arr.sort((a, b) => String(a._date || '').localeCompare(String(b._date || '')))
      }
      this.rows = arr
    },

    /* ---------------- 详情 ---------------- */
    /* 【P216】轻提示：优先用项目 notice，退化到 $toast */
    noticeOrToast(msg) {
      if (typeof this.notice === 'function') { this.notice(msg, 'error'); return; }
      if (this.$toast && this.$toast.fail) { this.$toast.fail(msg); return; }
      console.log(msg);
    },
    openDetail(row) {
      const sp = this.curSpec
      this.detailRow = row
      this.detailShow = true
      this.detailLoading = true
      this.detailGroups = []
      this.detailDoc = {}
      this.detailTitle = (sp ? sp.title : '单据') + '详情'
      this.detailCanApprove = false
      /* 【P207】备注状态复位；单据详情返回后回填 document.remark */
      this.detailRemark = ''
      this.detailRemarkEdit = null
      this.detailRemarkDraft = ''

      if (!sp) {
        this.detailLoading = false
        return
      }

      if (sp.detailType === 'approval') {
        /* 【P216】★ 必须用 approvalSnOf(row)（审批单 sn），不能用 row.sn（消息自身 sn）
           —— 后端 approvalDetail 按 sn 查 t_client_business_approval，用错必然 404「审批单不存在或已删除」。
           消息表的 approval_id 存的是审批单内部 id，approval_sn 由后端 enrichMessages 反查补齐。 */
        const __asn = this.approvalSnOf(row)
        if (!__asn || __asn === row.sn) {
          /* 非审批类消息（如「审批已通过」通知）没有审批单 ⇒ 降级为普通详情，不弹审批层 */
          this.detailLoading = false
          this.noticeOrToast('该消息为通知，无对应审批单')
          this.detailShow = false
          return
        }
        financeApi.financeApprovalDetail({ sn: __asn }).then(res => {
          if (!res || res.code !== 200) {
            this.detailLoading = false
            this.$toast.fail((res && res.msg) || '详情加载失败')
            return
          }
          const d = res.data || {}
          const a = d.approval || {}
          const bizName = { payment: '支付单', refund: '退款单', room: '会议室订单' }[d.biz] || '其他单据'
          const f = []
          const push = (l, v) => { if (v !== null && v !== undefined && v !== '') { f.push({ label: l, value: String(v) }) } }
          push('审批单号', a.sn)
          push('审批事由', a.task_descript)
          push('关联业务', bizName)
          push('提交人', a.create_user_name)
          push('审批人', a.approval_user_name || a.task_user_name)
          push('状态', a.status_name)
          push('提交时间', a.create_time)
          push('审批时间', a.update_time)
          /* 【P100】处理意见：通过='审批通过' / 免审='系统自动审核通过' / 驳回='审批未通过：{原因}' */
          push('处理意见', a.mssage)
          this.detailGroups = [{ name: '审批信息', fields: f }]
          this.detailTitle = '审批详情'
          this.detailCanApprove = !!a.can_approve

          const bizType = BIZ_TO_TYPE[d.biz]
          const docSn = a.source_sn || a.parent_sn
          if (bizType && docSn) {
            financeApi.financeDocDetail(bizType, docSn).then(r2 => {
              this.detailLoading = false
              if (r2 && r2.code === 200 && r2.data && r2.data.groups) {
                this.detailGroups = this.detailGroups.concat(r2.data.groups)
              }
            }).catch(() => { this.detailLoading = false })
            return
          }
          this.detailLoading = false
        }).catch(() => {
          this.detailLoading = false
          this.$toast.fail('详情加载失败')
        })
        return
      }

      financeApi.financeDocDetail(sp.detailType, row.sn).then(res => {
        this.detailLoading = false
        if (!res || res.code !== 200) {
          this.$toast.fail((res && res.msg) || '详情加载失败')
          return
        }
        const d = res.data || {}
        this.detailTitle = d.title || this.detailTitle
        this.detailGroups = d.groups || []
        /* 【P207】回填备注（后端 docDetail 返回 document 全字段） */
        this.detailDoc = d.document || {}
        this.detailRemark = (d.document && d.document.remark) || ''
        /* 【P205 2.0001】支付单详情页「审核 / 驳回」
         * ------------------------------------------------------------
         * 根因（此前支付单详情永无审核按钮）：支付单 detailType='payment'，走的是
         * financeDocDetail 分支，而该分支从不赋值 detailCanApprove（L1967 初始化为 false），
         * 只有 detailType==='approval' 的分支才会赋值 ⇒ 底部只渲染「关闭」。
         *
         * 修法：支付单详情额外按「业务单据 sn」反查其审批单（后端 approvalDetail 支持
         * biz_sn 入参，定位键 source_sn→parent_sn 与审批执行链路一致），拿到审批单 sn
         * 与 can_approve 后置真。approval_sn 一并存入 detailRow，供 onApprove/onReject 复用。
         *
         * 复用既有基建：不新写审批逻辑，仍走 financeApprovalApprove / financeApprovalReject
         * → 后端 ApprovalExecuteService（审核规则唯一出口）。 */
        /* 【P207】审核反查扩展到 支付单 / 退款单 / 发票（三者后端均下发 can_approve） */
        if (['payment', 'refund', 'invoice'].indexOf(sp.detailType) >= 0) {
          const localCan = row.can_approve === true
          const localSn = row.approval_sn || ''
          if (localSn) {
            this.detailCanApprove = localCan
            this.detailRow = Object.assign({}, row, { _approvalSn: localSn })
          }
          financeApi.financeApprovalDetail({ biz_sn: row.sn }).then(r2 => {
            if (!r2 || r2.code !== 200) return
            const a = (r2.data && r2.data.approval) || {}
            /* 服务端返回的 can_approve 是权威判据（校验 task_user_id + status===2） */
            this.detailCanApprove = !!a.can_approve
            if (a.sn) {
              this.detailRow = Object.assign({}, row, { _approvalSn: a.sn, _approval: a })
              this.detailApprovalText = a.status_name || ''
            }
          }).catch(() => {})
        }
      }).catch(() => {
        this.detailLoading = false
        this.$toast.fail('详情加载失败')
      })
    },

    /* ---------------- 【P207】详情备注 ---------------- */
    /** 进入编辑态（新增/修改同一入口） */
    startRemark() {
      this.detailRemarkDraft = this.detailRemark || ''
      this.detailRemarkEdit = 1
    },
    cancelRemark() {
      this.detailRemarkEdit = null
      this.detailRemarkDraft = ''
    },
    /** 保存备注：biz 由当前单据 detailType 决定 */
    saveRemark() {
      const sp = this.curSpec
      const row = this.detailRow
      if (!sp || !row || !row.sn) { this.$toast.fail('缺少单据信息'); return }
      const bizMap = { payment: 'payment', refund: 'refund', invoice: 'invoice' }
      const biz = bizMap[sp.detailType]
      if (!biz) { this.$toast.fail('该单据暂不支持备注'); return }
      if (this.remarkSaving) { return }
      this.remarkSaving = true
      financeApi.financeSaveDocRemark({ biz: biz, sn: row.sn, remark: this.detailRemarkDraft })
        .then(res => {
          this.remarkSaving = false
          if (!res || res.code !== 200) { this.$toast.fail((res && res.msg) || '备注保存失败'); return }
          this.detailRemark = this.detailRemarkDraft
          this.detailRemarkEdit = null
          this.$toast.success(res.msg || '备注已保存')
        })
        .catch(() => { this.remarkSaving = false; this.$toast.fail('备注保存异常') })
    },

    /* ---------------- 审批写链路 ---------------- */
    /* 【P205 2.0001】统一取「审批单 sn」：
     *   · 审核入口（detailType='approval'）→ row.sn 本身就是审批单 sn
     *   · 支付单（detailType='payment'）  → row.sn 是支付单 sn，审批单 sn 存在 row._approvalSn
     * 取不到时返回空串，由调用方提示，绝不误传业务 sn 到审批接口。 */
    approvalSnOf(row) {
      if (!row) return ''
      return row._approvalSn || (row._approval && row._approval.sn) || row.approval_sn || row.sn || ''
    },
    /* 审批事由文案（支付单无 task_descript，退到单据名/标题） */
    approvalLabelOf(row) {
      if (!row) return '该审批单'
      return (row._approval && row._approval.task_descript) || row.task_descript || row._title || row.name || '该审批单'
    },
    onApprove(row) {
      const label = this.approvalLabelOf(row)
      this.$dialog.confirm({ title: '审批通过', message: '确认通过「' + label + '」？' })
        .then(() => this.doApprove(row))
        .catch(() => {})
    },
    doApprove(row) {
      const asn = this.approvalSnOf(row)
      if (!asn) { this.$toast.fail('该单据暂无审批单，无法审核'); return }
      const t = this.$toast.loading({ duration: 0, forbidClick: true, message: '提交中...' })
      financeApi.financeApprovalApprove({ approvalsn: asn }).then(res => {
        t.clear()
        if (res && res.code === 200) {
          this.$toast.success(res.msg || '审批已通过')
          this.detailShow = false
          this.exitBatch()
          this.afterApprove()
        } else {
          this.$toast.fail((res && res.msg) || '审批失败')
        }
      }).catch(() => { t.clear(); this.$toast.fail('审批失败') })
    },
    /* 【P100】驳回：改为弹「必填原因」输入层；原实现只弹「确认驳回？」，原因无从填写 */
    onReject(row) {
      this.rejectRow = row
      this.rejectLabel = this.approvalLabelOf(row)
      this.rejectRemark = ''
      this.rejectShow = true
    },
    /* 【P214】van-dialog 的 before-close(action, done)：
       Vant 2.13.9 的 Dialog 内部只认**第二个形参 done 回调**（用于复位 loading.confirm），
       **函数返回值被完全丢弃**。原实现只 return Promise / return true|false
       ⇒ loading.confirm 永不复位 ⇒ 驳回按钮永久转圈，只能刷新页面。
       改法：签名补 done()，所有分支都必须回调（成功 done() 关闭 / 失败 done(false) 保持打开）。 */
    onRejectBeforeClose(action, done) {
      if (action !== 'confirm') { done(); return }
      const remark = (this.rejectRemark || '').trim()
      if (remark === '') {
        this.$toast.fail('请填写驳回原因')
        done(false)
        return
      }
      const row = this.rejectRow
      /* 【P205 2.0001】批量驳回：rejectRow 只是占位（取第一条用于文案），真正要驳回的是 batchRejectRows */
      if (this.batchRejectRows && this.batchRejectRows.length) {
        const picked = this.batchRejectRows.slice()
        this.batchRejectRows = []
        this.runBatchReject(picked, remark)
        done()
        return
      }
      const asn = this.approvalSnOf(row)
      if (!asn) {
        this.$toast.fail('该单据暂无审批单，无法驳回')
        done(false)
        return
      }
      const t = this.$toast.loading({ duration: 0, forbidClick: true, message: '提交中...' })
      financeApi.financeApprovalReject({ approvalsn: asn, remark: remark }).then(res => {
        t.clear()
        if (res && res.code === 200) {
          this.$toast.success((res.data && res.data.mssage) ? res.data.mssage : (res.msg || '已驳回'))
          this.detailShow = false
          this.exitBatch()
          this.afterApprove()
          done()
        } else {
          this.$toast.fail((res && res.msg) || '驳回失败')
          done(false)
        }
      }).catch(() => {
        t.clear()
        this.$toast.fail('驳回失败')
        done(false)
      })
    },
    /* ---------------- 【P205 2.0001】列表批量审核 ---------------- */
    /** 该行是否可被勾选：必须有审批单 + 服务端判定 can_approve（审批人 + 待审核） */
    rowCanApprove(row) {
      return !!(row && row.approval_sn && row.can_approve === true)
    },
    toggleBatch() {
      this.batchMode = !this.batchMode
      if (!this.batchMode) { this.batchChecked = [] }
    },
    exitBatch() {
      this.batchMode = false
      this.batchChecked = []
    },
    onBatchCheck(row) {
      if (!this.rowCanApprove(row)) return
      const sn = row.sn
      const i = this.batchChecked.indexOf(sn)
      if (i >= 0) { this.batchChecked.splice(i, 1) } else { this.batchChecked.push(sn) }
    },
    /** 批量勾选 = 当前列表里所有「可审核」的行 */
    toggleBatchAll() {
      const all = this.rows.filter(r => this.rowCanApprove(r)).map(r => r.sn)
      this.batchChecked = this.batchChecked.length === all.length ? [] : all
    },
    /** 批量动作：action = approve | reject */
    batchAction(action) {
      const picked = this.rows.filter(r => this.batchChecked.indexOf(r.sn) >= 0)
      if (!picked.length) { this.$toast.fail('请先选择要处理的支付单'); return }
      if (action === 'reject') {
        /* 驳回需要填原因：复用详情页的必填弹层，逐条提交时沿用同一原因 */
        this.rejectRow = picked[0]
        this.rejectLabel = '已选 ' + picked.length + ' 条支付单'
        this.rejectRemark = ''
        this.batchRejectRows = picked
        this.rejectShow = true
        return
      }
      const label = picked.length === 1 ? this.approvalLabelOf(picked[0]) : ('已选 ' + picked.length + ' 条支付单')
      this.$dialog.confirm({ title: '批量审核通过', message: '确认通过「' + label + '」？' })
        .then(() => this.runBatchApprove(picked))
        .catch(() => {})
    },
    runBatchApprove(picked) {
      const t = this.$toast.loading({ duration: 0, forbidClick: true, message: '提交中...' })
      let ok = 0, fail = 0
      const seq = picked.reduce((p, row) => p.then(() =>
        financeApi.financeApprovalApprove({ approvalsn: this.approvalSnOf(row) }).then(res => {
          if (res && res.code === 200) { ok++ } else { fail++ }
        }).catch(() => { fail++ })
      ), Promise.resolve())
      seq.then(() => {
        t.clear()
        if (fail === 0) { this.$toast.success('已通过 ' + ok + ' 条') }
        else { this.$toast.fail('通过 ' + ok + ' 条，失败 ' + fail + ' 条') }
        this.exitBatch()
        this.afterApprove()
      })
    },
    runBatchReject(picked, remark) {
      const t = this.$toast.loading({ duration: 0, forbidClick: true, message: '提交中...' })
      let ok = 0, fail = 0
      const seq = picked.reduce((p, row) => p.then(() =>
        financeApi.financeApprovalReject({ approvalsn: this.approvalSnOf(row), remark: remark }).then(res => {
          if (res && res.code === 200) { ok++ } else { fail++ }
        }).catch(() => { fail++ })
      ), Promise.resolve())
      seq.then(() => {
        t.clear()
        if (fail === 0) { this.$toast.success('已驳回 ' + ok + ' 条') }
        else { this.$toast.fail('驳回 ' + ok + ' 条，失败 ' + fail + ' 条') }
        this.exitBatch()
        this.afterApprove()
      })
    },
    afterApprove() {
      this.reload()
      this.refreshTodo()
    },

    /* ---------------- 统计 ---------------- */
    loadStat() {
      this.statLoading = true
      financeApi.financeStatisticsDashboard({}).then(res => {
        this.statLoading = false
        if (res && res.code === 200 && res.data) { this.stat = res.data }
      }).catch(() => { this.statLoading = false })
    },

    onLogout() {
      this.$dialog.confirm({ title: '提示', message: '确认退出登录？' })
        .then(() => { logout() })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.fn2-page {
  /* 【P198】修复 01：财务端列表不能向下滚动。
     ⚠️ 根因：index.html 内联样式里 `#app { overflow: hidden; }` 把整页裁掉，
     页面自身永远不能滚动（lib.min.css 同样把 #app/body/html 设为 height:100%;overflow:hidden）。
     销售端由 .client-root 承担滚动（client.vue L7572），财务端此前没有任何滚动容器，
     .list-box 是 height:auto/overflow:visible，于是列表被父级直接裁掉 = 「滑不动」。
     ⇒ 修复方式与销售端完全一致：让根容器自己成为滚动容器。 */
  /* ============================================================
     【P296】★★ 修复「下拉刷新无反应 / 报 JS 错」
     根因（CDP 实测取证，非推测）：
       van-pull-refresh 实例的 `scroller === null`，
       且 .fn2-page 的 scrollHeight === clientHeight（内容不撑高，永远没有可下拉空间）
       ⇒ Vant 的 getScroller 找不到「内容溢出」的滚动容器 ⇒ 触摸被直接忽略，
         status 恒为 'normal'，@refresh 从不触发。
     为什么找不到：.fn2-page 自己就是滚动容器，而它内容不满一屏 ⇒ 不溢出。
     方案（与销售端 client.vue:9172-9180 完全同口径）：
       **让 .van-pull-refresh 自己成为滚动容器** —— Vant 的 getScroller 从元素自身
       开始向上找第一个 overflow-y 命中 scroll|auto|overlay 的节点，
       「自身 overflow-y:auto」时 scrollEl 就是它自己，scrollTop 恒 0 ⇒ 下拉必然可触发。
     配套：.fn2-page 改 flex 列布局并去掉自身滚动（避免两层滚动容器互相抢手势）。
     ============================================================ */
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--page-bg, #f5f6f8);
  padding-bottom: 0;
  box-sizing: border-box;
}
/* 下拉刷新区：自身即滚动容器（销售端同款写法） */
.fn2-page > .van-pull-refresh{
  flex:1 1 auto; min-height:0; overflow-y:auto; -webkit-overflow-scrolling:touch;
  display:flex; flex-direction:column;
}
.fn2-page > .van-pull-refresh > .van-pull-refresh__track{ display:flex; flex-direction:column; flex:1 1 auto; min-height:0; }
/* 头部等固定块不参与 flex:1 */
.fn2-page > .customer-header{ flex:0 0 auto; }
.fn2-page > .customer-header-spacer{ flex:0 0 auto; }
/* 底部仍需为 tabbar 让位（原先靠 .fn2-page 的 padding-bottom，改为给列表内容加内边距） */
.fn2-page .list-box{ padding-bottom: 66px; }

/* ============================================================
 * 【P301 全站消息统一】财务端消息容器（走 MessagePanel 组件）
 *   与 P296 的滚动布局同口径：容器自身 flex 撑高，内部组件自己滚。
 * ============================================================ */
.fn2-msgwrap{ flex:1 1 auto; min-height:0; display:flex; flex-direction:column; overflow:hidden; }
.fn2-msgwrap .mp{ flex:1 1 auto; min-height:0; }


/* ---------------- 【P202/P203】头部「筛选 / 排序」两枚胶囊按钮 ---------------- */
/* ⚠️ 基类 .customer-header__btn 定义在 client.vue 的全局 <style>（非 scoped）里，
   是「竖排图标 + 10px 文字」布局 —— **不改它**（会波及销售端）。
   P203 起财务端**不再复用该类**，改为自建 .fn2-hbtn 胶囊。 */

/* 按钮组：紧贴搜索框右侧，与搜索框同高（34px），右缘与页面 16px 内边距对齐 */
.fn2-hacts {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* 胶囊按钮本体：高度 34px 与搜索框齐平，横向内边距 10px */
.fn2-hbtn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  height: 34px;
  padding: 0 10px;
  border-radius: 17px;
  background: #f2f3f5;
  color: #4e5969;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  transition: background .18s, color .18s;
}
.fn2-hbtn__ico {
  font-size: 14px;
  line-height: 1;
}
/* 【P244】消息 Tab 红色气泡角标 */
.fn2-tab-badge {
  display: inline-block;
  min-width: 15px; height: 15px; padding: 0 4px;
  margin-left: 4px; border-radius: 8px;
  background: #d93026; color: #fff;
  font-size: 10px; line-height: 15px; text-align: center;
  font-style: normal; vertical-align: 1px;
}
.fn2-hbtn__txt {
  font-size: 12px;
}
/* 激活态：浅蓝底 + 蓝图标蓝字（与「已筛选/已排序」语义绑定） */
.fn2-hbtn--on {
  background: #eaf0fd;
  color: #2b5fd9;
}
/* 按下反馈 */
.fn2-hbtn:active {
  background: #e5e6eb;
}
.fn2-hbtn--on:active {
  background: #dbe6fb;
}

/* 【P203b】「已选 N 项」角标。
   ⚠️ 旧实现 left:20px + top:3px：20px 落在「筛」字上，实测压字。
   现方案：水平锚定**漏斗图标右上角**（left:9px，图标占 0~14px），
   垂直**溢出胶囊顶部**（top:-5px）⇒ 既不压字，也不侵入右侧「排序」按钮。 */
.fn2-hbtn__badge {
  position: absolute;
  top: -5px;
  left: 9px;
  min-width: 15px;
  height: 15px;
  line-height: 15px;
  padding: 0 4px;
  border-radius: 8px;
  background: #f53f3f;
  color: #fff;
  font-size: 9px;
  font-style: normal;
  font-weight: 500;
  text-align: center;
  box-sizing: border-box;
  box-shadow: 0 0 0 1.5px #fff;
}
/* 激活态：胶囊变浅蓝，角标描边跟底色（视觉上「贴」在按钮上） */
.fn2-hbtn--on .fn2-hbtn__badge {
  box-shadow: 0 0 0 1.5px #eaf0fd;
}

/* 搜索框：弹性占满剩余空间（不再硬卡 150px），最小 96px 保证可输入 */
.customer-header__top .customer-header__search {
  flex: 1;
  min-width: 96px;
  max-width: none;
}

/* ---------------- 【P202】排序面板「当前：」说明行 ---------------- */
.fn2-sortcur {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  margin: 0 16px 6px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f5f6f8;
  font-size: 12px;
}
.fn2-sortcur__lb {
  color: #8c8f94;
}
.fn2-sortcur__val {
  color: #2b5fd9;
  font-weight: 500;
}

/* ---------------- 权限不足态 ---------------- */
.fn2-denied {
  padding: 64px 24px 24px;
  text-align: center;
}
.fn2-denied__ic {
  font-size: 50px;
  color: #f0a020;
  line-height: 1;
  margin-bottom: 14px;
}
.fn2-denied__t {
  font-size: 17px;
  font-weight: 600;
  color: #1d2129;
  margin-bottom: 8px;
}
.fn2-denied__m {
  font-size: 13px;
  color: #86909c;
  margin-bottom: 18px;
}
.fn2-denied__card {
  background: #fff;
  border-radius: 14px;
  padding: 4px 14px;
  text-align: left;
  margin-bottom: 14px;
  box-shadow: 0 4px 20px rgba(31, 58, 110, 0.06);
}
.fn2-denied__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 11px 0;
  font-size: 13px;
  color: #86909c;
  border-bottom: 1px solid #f5f6f8;
}
.fn2-denied__row:last-child { border-bottom: none; }
.fn2-denied__row b { color: #1d2129; font-weight: 500; }
.fn2-denied__hint {
  font-size: 12px;
  color: #a8adb8;
  line-height: 1.7;
  text-align: left;
  margin-bottom: 20px;
  padding: 10px 12px;
  background: #f7f8fa;
  border-radius: 10px;
}
.fn2-denied .van-button + .van-button { margin-top: 10px; }

/* ---------------- 首页 Hero ---------------- */
.fn2-hero {
  background: linear-gradient(135deg, #2b5fd9 0%, #4a86f0 100%);
  padding: 22px 16px 56px;
  color: #fff;
}
.fn2-hero__top { display: flex; align-items: center; gap: 10px; }
.fn2-avatar {
  width: 44px; height: 44px; border-radius: 50%;
  background: rgba(255, 255, 255, 0.22);
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; font-weight: 600; flex: 0 0 44px;
}
.fn2-hero__id { flex: 1; min-width: 0; }
.fn2-hero__name { font-size: 17px; font-weight: 600; }
.fn2-hero__dept { font-size: 12px; opacity: 0.82; margin-top: 3px; }
.fn2-hero__role {
  font-size: 12px; padding: 3px 10px; border-radius: 12px;
  background: rgba(255, 255, 255, 0.22);
}
.fn2-kpis { display: flex; gap: 10px; margin-top: 18px; }
.fn2-kpi {
  flex: 1; background: rgba(255, 255, 255, 0.16);
  border-radius: 12px; padding: 10px 12px;
}
.fn2-kpi__n { font-size: 21px; font-weight: 700; line-height: 1.2; }
.fn2-kpi__l { font-size: 12px; opacity: 0.85; margin-top: 2px; }

/* ---------------- 快捷入口 ---------------- */
.fn2-quick {
  margin: -40px 14px 0;
  background: #fff;
  border-radius: 16px;
  padding: 14px 14px 4px;
  box-shadow: 0 4px 20px rgba(31, 58, 110, 0.08);
  position: relative;
  z-index: 2;
}
.fn2-quick__hd {
  font-size: 14px; font-weight: 600; color: #1d2129;
  margin-bottom: 12px;
  display: flex; align-items: center; justify-content: space-between;
}
.fn2-quick__refresh { font-size: 12px; font-weight: 400; color: #2b5fd9; cursor: pointer; }
.fn2-grid { display: flex; flex-wrap: wrap; }
.fn2-tile {
  width: 33.333%; padding: 10px 0 14px;
  display: flex; flex-direction: column; align-items: center;
  cursor: pointer;
}
.fn2-tile:active { opacity: 0.65; }
.fn2-tile__ic {
  position: relative;
  width: 44px; height: 44px; border-radius: 14px;
  background: #f0f4ff;
  display: flex; align-items: center; justify-content: center;
  /* ★ v-html 注入的 svg 拿不到 scoped 属性，颜色靠 currentColor 继承 */
  color: #2b5fd9;
}
.fn2-tile__svg { display: flex; align-items: center; justify-content: center; }
.fn2-tile__svg svg { stroke: currentColor; fill: none; }
.fn2-tile__label { font-size: 12px; color: #4e5969; margin-top: 7px; }
.fn2-tile__dot {
  position: absolute; top: -5px; right: -5px;
  min-width: 17px; height: 17px; line-height: 17px;
  padding: 0 4px; border-radius: 9px;
  background: #f53f3f; color: #fff;
  font-size: 10px; font-weight: 600; text-align: center;
  box-shadow: 0 0 0 2px #fff;
  box-sizing: border-box;
}

/* ---------------- 账号区 ---------------- */
.fn2-acct { margin-top: 6px; border-top: 1px solid #f5f6f8; }
.fn2-acct__row {
  display: flex; align-items: center; gap: 10px;
  padding: 13px 2px; cursor: pointer;
}
.fn2-acct__row:active { opacity: 0.65; }
.fn2-acct__ic { color: #2b5fd9; display: flex; align-items: center; }
.fn2-acct__ic svg { stroke: currentColor; fill: none; }
.fn2-acct__txt { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.fn2-acct__label { font-size: 14px; color: #1d2129; }
.fn2-acct__sub { font-size: 11px; color: #a8adb8; margin-top: 2px; }
.fn2-acct__arrow { color: #c0c4cc; }
.fn2-tip { font-size: 11px; color: #a8adb8; text-align: center; padding: 6px 0 12px; }

/* ---------------- 列表头部返回箭头 ---------------- */
.fn2-topback {
  font-size: 17px; color: #1d2129; margin-right: 4px;
  cursor: pointer; font-weight: 600;
}

/* ---------------- 分段（支付单 / 回款计划） ---------------- */
.fn2-seg {
  display: flex; gap: 8px; padding: 12px 14px 0;
}
.fn2-seg__i {
  font-size: 13px; color: #4e5969;
  padding: 5px 14px; border-radius: 15px;
  background: #fff; cursor: pointer;
  border: 1px solid #e5e6eb;
}
.fn2-seg__i--on {
  color: #2b5fd9; border-color: rgba(43, 95, 217, 0.35);
  background: rgba(43, 95, 217, 0.08); font-weight: 600;
}

/* ---------------- 共 N 条 / 合计（对齐销售端 cd-tabbar__summary） ---------------- */
/* 【P116】销售人员筛选条 */
.fn2-owner { display: flex; align-items: center; gap: 8px; margin: 8px 14px 0; padding: 8px 12px;
  background: #fff; border-radius: 12px; font-size: 13px; box-shadow: 0 2px 10px rgba(20, 40, 80, .05); }
.fn2-owner__lb { color: #86909c; font-size: 12px; flex: none; }
.fn2-owner__val { flex: 1; color: #1d2129; font-weight: 600; cursor: pointer; }
.fn2-owner__val--ph { color: #b6bcc6; font-weight: 400; }
.fn2-owner__arrow { font-style: normal; margin-left: 4px; color: #969799; font-size: 11px; }
.fn2-owner__clear { flex: none; color: #2b5fd9; font-size: 12px; }

/* 【P205 2.0001】支付单批量审核：入口胶囊 + 批量条 + 行勾选 */
.fn2-owner__batch {
  flex: none; margin-left: auto;
  padding: 3px 10px; border-radius: 11px;
  background: #eaf0fd; color: #2b5fd9;
  font-size: 12px; font-weight: 600; cursor: pointer;
}
.fn2-owner__batch--on { background: #2b5fd9; color: #fff; }
.fn2-batchbar {
  display: flex; flex-direction: column; gap: 8px;
  padding: 10px 16px; margin-bottom: 8px;
  background: #f6f8fc; border-bottom: 1px solid #e8eef8;
}
.fn2-batchbar__txt { font-size: 12px; color: #4e5969; font-weight: 600; }
.fn2-batchbar__ops { display: flex; flex-wrap: wrap; gap: 8px; }
.fn2-bbtn {
  padding: 6px 12px; border: 1px solid #dcdfe6; border-radius: 6px;
  background: #fff; color: #4e5969; font-size: 12px; line-height: 1.2;
  cursor: pointer; -webkit-tap-highlight-color: transparent;
}
.fn2-bbtn:active { background: #f2f3f5; }
.fn2-bbtn--primary { background: #2b5fd9; border-color: #2b5fd9; color: #fff; }
.fn2-bbtn--danger { background: #fff; border-color: #f5c2c7; color: #e34d59; }
.fn2-item { position: relative; }
.fn2-item--batch .item-head { padding-left: 40px; }
.fn2-check {
  position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
  width: 20px; height: 20px; border-radius: 50%;
  border: 1.5px solid #c9cdd4; background: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; color: transparent; z-index: 2; cursor: pointer;
}
.fn2-check.on { background: #2b5fd9; border-color: #2b5fd9; color: #fff; }
.fn2-check.off { background: #f2f3f5; border-color: #e5e6eb; cursor: not-allowed; }

/* 【P187】合计条：左「共 N 条」+ 右「合计金额 + 显示/隐藏开关（按列表记忆、默认关）」 */
.fn2-sum {
  display: flex; justify-content: space-between; align-items: center;
  padding: 10px 16px 4px;
  font-size: 12px; color: #86909c;
}
.fn2-sum__right { display: inline-flex; align-items: center; gap: 8px; }
.fn2-sum__label { color: #86909c; }
.fn2-sum__val { font-size: 13px; font-weight: 600; color: #e34d59; }
.fn2-sum__toggle {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 9px; border-radius: 11px;
  background: #f2f3f5; color: #86909c; font-size: 11px;
  cursor: pointer; user-select: none;
}
.fn2-sum__toggle--on { background: #eaf0fd; color: #2b5fd9; }
.fn2-sum__dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: #c9cdd4; display: inline-block;
}
.fn2-sum__toggle--on .fn2-sum__dot { background: #2b5fd9; }

/* 生效筛选条复用全局 .filter-status-bar */

/* ---------------- 空态（对齐销售端 .bl-empty 观感） ---------------- */
.fn2-empty2 {
  margin-top: 20px; padding: 56px 24px; text-align: center;
}
.fn2-empty2__ic {
  font-size: 46px; color: #ccd2dc; line-height: 1; margin-bottom: 12px;
}
.fn2-empty2__txt { font-size: 14px; color: #8c8f94; }
.fn2-empty2__hint { font-size: 12px; color: #c0c4cc; margin-top: 6px; }

/* ---------------- 统计 ---------------- */
.fn2-stat { padding: 0 14px 14px; }
.fn2-subbar {
  display: flex; align-items: center; gap: 2px;
  padding: 13px 0 12px; background: #fff;
  margin: 0 -14px 12px; padding-left: 14px;
}
.fn2-subbar__t { font-size: 15px; font-weight: 600; color: #1d2129; }
.fn2-stat__range {
  display: flex; justify-content: space-between; align-items: center;
  font-size: 12px; color: #86909c; margin-bottom: 12px;
}
.fn2-stat__range .van-icon { font-size: 17px; color: #2b5fd9; }
.fn2-stat__grid { display: flex; flex-wrap: wrap; gap: 10px; }
.fn2-stat__card {
  width: calc(50% - 5px); box-sizing: border-box;
  background: #fff; border-radius: 14px; padding: 14px;
  box-shadow: 0 4px 20px rgba(31, 58, 110, 0.06);
}
.fn2-stat__label { font-size: 12px; color: #86909c; }
.fn2-stat__value { font-size: 18px; font-weight: 700; color: #1d2129; margin-top: 6px; }
.fn2-stat__extra { font-size: 11px; color: #a8adb8; margin-top: 4px; }

/* ---------------- 弹窗 ---------------- */
.fn2-loading { padding: 30px 0; text-align: center; }
.fn2-empty { padding: 40px 0; text-align: center; font-size: 13px; color: #a8adb8; }
.fn2-sheet { display: flex; flex-direction: column; height: 100%; background: #f5f6f8; }
.fn2-sheet__hd {
  display: flex; justify-content: space-between; align-items: center;
  padding: 15px 16px; font-size: 15px; font-weight: 600; color: #1d2129;
  background: #fff;
}
.fn2-sheet__hd .van-icon { font-size: 18px; color: #86909c; }
.fn2-sheet__bd { flex: 1; overflow-y: auto; padding: 12px 14px 24px; }
.fn2-sheet__ft {
  display: flex; gap: 10px; padding: 10px 14px 18px;
  background: #fff; border-top: 1px solid #f5f6f8;
}

/* 【P100】审批驳回：原因输入弹层 */
.fn2-reject { padding: 6px 4px 4px; }
.fn2-reject__tip {
  font-size: 13px; color: #4e5969; line-height: 1.7;
  padding: 0 12px 8px;
}
.fn2-reject .van-field {
  background: #f7f8fa; border-radius: 10px;
  padding: 8px 12px;
}

/* 高级筛选行 */
.fn2-adv {
  display: flex; justify-content: space-between; align-items: center;
  background: #fff; border-radius: 12px; padding: 14px;
  margin-bottom: 10px;
}
.fn2-adv__label { font-size: 13px; color: #4e5969; }
.fn2-adv__val { font-size: 13px; color: #1d2129; }
.fn2-adv__val--ph { color: #c0c4cc; }
/* 【P175】日期区间取值可能较长，允许换行右对齐 */
.fn2-adv__val { max-width: 62%; text-align: right; word-break: break-all; }
/* 【P187】面板内联「×」清除按钮（日期/关联单号/门店/会议室 支持） */
.fn2-adv__arrow {
  font-style: normal; margin-left: 6px;
  color: #c0c4cc; font-size: 14px; line-height: 1;
}
.fn2-adv__arrow:active { color: #e34d59; }

/* ---------------- 【P200】筛选面板内的「是否显示合计金额」开关行 ----------------
   与列表头 .fn2-sum__toggle 同一状态；点击整行右侧区域即可切换（命中面积大、好点）。 */
.fn2-adv--switch { flex-wrap: wrap; }
.fn2-adv--switch .fn2-adv__label { flex: 1 1 auto; }
.fn2-adv__val--switch {
  flex: 0 0 auto; display: inline-flex; align-items: center; gap: 8px;
  max-width: none; cursor: pointer; -webkit-tap-highlight-color: transparent;
}
.fn2-adv__sw {
  position: relative; display: inline-block; width: 40px; height: 22px;
  background: #e5e6eb; border-radius: 11px; transition: background .2s;
}
.fn2-adv__sw--on { background: #2b5fd9; }
.fn2-adv__sw-dot {
  position: absolute; top: 2px; left: 2px; width: 18px; height: 18px;
  background: #fff; border-radius: 50%; transition: left .2s;
  box-shadow: 0 1px 2px rgba(0,0,0,.18);
}
.fn2-adv__sw--on .fn2-adv__sw-dot { left: 20px; }
.fn2-adv__sw-txt { font-size: 13px; color: #4e5969; }
.fn2-adv__hint {
  flex: 0 0 100%; margin-top: 6px;
  font-size: 11px; color: #86909c; line-height: 1.5;
}

/* ---------------- 【P187】通用选择器（关联单号 / 门店 / 会议室） ---------------- */
.fn2-sheet--pick .fn2-sheet__bd { padding: 0 0 12px; }
.fn2-pick__search { padding: 4px 12px 8px; border-bottom: 1px solid #f5f6f8; }
.fn2-pick__bd { padding: 6px 0 12px; }
.fn2-pick__item {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px; border-bottom: 1px solid #f7f8fa;
  cursor: pointer;
}
.fn2-pick__item:active { background: #f7f8fa; }
.fn2-pick__item--on .fn2-pick__txt { color: #2b5fd9; font-weight: 600; }
.fn2-pick__main { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.fn2-pick__txt {
  font-size: 14px; color: #1d2129;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.fn2-pick__sub { font-size: 11px; color: #a8adb8; }
.fn2-pick__ok { color: #2b5fd9; font-size: 17px; flex: none; }

/* ---------------- 单据详情（分组字段） ---------------- */
.ro-group { margin-bottom: 12px; }
.ro-group--card { background: #fff; border-radius: 12px; padding: 4px 14px; }
.ro-group__hd {
  font-size: 13px; font-weight: 600; color: #2b5fd9;
  padding: 10px 0 6px;
}
.ro-field {
  display: flex; justify-content: space-between; align-items: flex-start;
  padding: 9px 0; border-bottom: 1px solid #f5f6f8;
  font-size: 13px;
}
.ro-field:last-child { border-bottom: none; }
.ro-label { color: #86909c; flex: 0 0 40%; }
.ro-val { color: #1d2129; flex: 1; text-align: left; word-break: break-all; }
.ro-val--right { text-align: right; }

/* ============================================================
   【P184】列表卡片信息行 —— 1:1 对齐销售端 client.vue
   （用户需求：「有列表的列需要跟销售端一样」）

   ★★ 重要：`.customer-card*` / `.status-tag` / `.tag-wait|receive|danger|allot` /
      `.tag-free` / `.tag-charge` / `.roomorder-flags` 这些类**已在 client.vue 的
      `<style>`（非 scoped）中全局定义**，财务端可直接复用，**不得在此重复定义**
      —— 财务端是 `<style scoped>`，重复定义会产生 data-v 属性、特异性高于全局，
      反而覆盖掉全局口径，破坏「与销售端 1:1」。
   ⇒ 本处**只补全局缺失的** `.item-flags`（对齐销售端 `.roomorder-flags` 的容器语义）。
   ============================================================ */

/* 标签行：收费/免费 + 审核状态（容器对齐销售端 .roomorder-flags / .contract-status-row） */
.item-flags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 5px; }
.item-flags .status-tag { margin: 0; }

/* ============================================================
   【P198】修复 03：去掉财务端列表卡片左侧蓝色竖条
   ⚠️ 根因：全局 `.crm-item`（client.vue L7669，非 scoped）含
      `border-left: 4px solid #2b5fd9;`，财务端列表项直接复用了该类。
   先例：client.vue L8166-8172 `.customer-item.crm-item { border-left:none; }`、
         bill-list.css L124-129 `.bill-list .crm-item { ... }`。
   此处用财务端独有容器类 `.fn2-item` 提高特异性，避免影响其它端。
   ============================================================ */
.fn2-item.crm-item {
  border-left: none;
}

/* 【P198】修复 02：详情弹层底部操作条。
   弹层主体（头部/hero/分组卡片）已改用全局 .detail-sheet* 系列，
   底部「驳回/通过/关闭」沿用财务端原有 .fn2-sheet__ft，此处仅补白底 + 分隔线。 */
.fn2-sheet__ft--solid {
  background: #fff;
  border-top: 1px solid #eef0f3;
  padding: 10px 14px calc(10px + env(safe-area-inset-bottom, 0px));
}
.fn2-sheet__ft--solid .van-button {
  height: 42px;
  border-radius: 21px;
  font-size: 14px;
}
/* ============================================================
 * 【P207】详情「添加备注」
 *   注：全屏侧滑用到的 .cust-detail-pop / .cust-detail__* 由全局 app.css
 *   （webpack ExtractTextPlugin allChunks:true ⇒ 单文件全量注入）提供，无需重复定义。
 * ============================================================ */
.fn2-remark-add {
  padding: 12px 0;
  font-size: 14px;
  color: #2b5fd9;
  cursor: pointer;
}
.fn2-remark-add:active { opacity: .6; }
.fn2-remark-edit { padding: 8px 0; }
.fn2-remark-ip {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  font-size: 14px;
  color: #1d2129;
  line-height: 1.5;
  outline: none;
  resize: none;
  background: #f7f8fa;
}
.fn2-remark-ip:focus { border-color: #2b5fd9; background: #fff; }
.fn2-remark-ops {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
}
/* ===== 【P207】详情视觉重构（全屏侧滑详情专用） =====
 * 背景：`.detail-hero / .detail-sec / .detail-card / .cust-detail*` 在 client.vue 里是 scoped 样式，
 *       财务端复用同名类拿不到样式 ⇒ 详情呈现「裸表格」（无卡片/无渐变/标签拆字/大片留白）。
 * 做法：本页 scoped 内自带一套同名样式，特异性高于全局，彻底不依赖别的页面。
 */
.cust-detail-pop { background: #f4f6fa; }
.cust-detail__nav {
  height: 48px; display: flex; align-items: center; padding: 0 12px;
  background: #fff; box-shadow: 0 1px 0 #eef0f4;
}
.cust-detail__back { width: 74px; font-size: 15px; color: #1d2129; cursor: pointer; }
.cust-detail__title { flex: 1; text-align: center; font-size: 17px; font-weight: 500; color: #1d2129; }
.cust-detail__action { width: 74px; text-align: right; font-size: 14px; font-weight: 500; color: #2f6bff; cursor: pointer; }
.cust-detail__body { flex: 1; overflow-y: auto; padding-bottom: 12px; }

/* 顶部信息卡（蓝渐变） */
.detail-hero {
  margin: 12px 14px 0; padding: 16px; border-radius: 14px;
  background: linear-gradient(135deg, #2f6bff 0%, #6f9bff 100%);
  box-shadow: 0 6px 18px rgba(47, 107, 255, .22);
}
.detail-hero__name {
  font-size: 17px; font-weight: 600; color: #fff; line-height: 1.45;
  /* 【P342-UI】超长单据标题防逐字断行（财务端 hero 是纵向块，本身不被挤压，
     但默认 break-all 会让长中文标题一字一行） */
  word-break: break-word; overflow-wrap: anywhere;
}
.detail-hero__sub { margin-top: 7px; font-size: 12px; color: rgba(255, 255, 255, .88); line-height: 1.4; }

/* 分组 */
.detail-sec { margin: 14px 14px 0; }
.detail-sec__title {
  display: flex; align-items: center; gap: 6px; margin-bottom: 8px;
  font-size: 14px; font-weight: 600; color: #1d2129;
}
.detail-sec__bar {
  display: inline-block; width: 3px; height: 14px; border-radius: 2px;
  background: #2f6bff; flex: none;
}

/* 字段卡片 */
.detail-card {
  background: #fff; border-radius: 12px; padding: 2px 14px;
  box-shadow: 0 1px 4px rgba(31, 58, 110, .06);
}
.detail-card__row {
  display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
  padding: 11px 0; border-bottom: 1px solid #f2f3f5;
}
.detail-card__row:last-child { border-bottom: none; }
/* ★ 标签不再定宽：原「支付单名称」被拆成两行是固定宽度所致 */
.detail-card__label { flex: none; font-size: 13px; color: #86909c; line-height: 20px; white-space: nowrap; }
.detail-card__val {
  flex: 1; min-width: 0; font-size: 13px; color: #1d2129; line-height: 20px;
  text-align: right; word-break: break-all;
}
.detail-empty {
  margin: 0 14px;
  padding: 40px 0; text-align: center; font-size: 13px; color: #c0c4cc;
}

/* 备注：空态做成「虚线按钮」，避免占掉一整块空白 */
.fn2-remark-add {
  display: flex; align-items: center; justify-content: center; width: 100%;
  margin: 8px 0; padding: 11px 0; font-size: 13px; font-weight: 500;
  color: #2f6bff; background: #f4f7ff;
  border: 1px dashed #bcd0ff; border-radius: 10px; cursor: pointer;
}
.fn2-remark-add:active { opacity: .7; }
.fn2-remark-edit { padding: 10px 0; }
.fn2-remark-ip {
  width: 100%; box-sizing: border-box; padding: 10px 12px;
  border: 1px solid #e5e6eb; border-radius: 10px; background: #f7f8fa;
  font-size: 14px; color: #1d2129; line-height: 1.5; outline: none; resize: none;
}
.fn2-remark-ip:focus { border-color: #2f6bff; background: #fff; }
.fn2-remark-ops { display: flex; justify-content: flex-end; gap: 10px; margin-top: 10px; }

/* 底部操作条：驳回=红描边、通过=主色实心（不再用绿色，与销售端主色统一） */
.fn2-sheet__ft--solid {
  display: flex; gap: 10px; padding: 10px 14px calc(10px + env(safe-area-inset-bottom, 0px));
  background: #fff; box-shadow: 0 -1px 0 #f0f1f3;
}
.fn2-sheet__ft--solid .van-button { flex: 1; height: 44px; border-radius: 22px; font-size: 15px; font-weight: 500; }
.fn2-sheet__ft--solid .van-button--primary { background: #2f6bff; border-color: #2f6bff; }
</style>



// WEBPACK FOOTER //
// src/pages/finance/index.vue